// Custom hook para bloquear navegación interna en React Router v6
import React, { useState, useEffect, useRef } from 'react';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { 
  ArrowBack, 
  Add, 
  Delete, 
  Business, 
  Person, 
  Home, 
  LocalShipping, 
  Build,
  Receipt,
  ExpandMore,
  Edit,
  Visibility,
  Download,
  Email,
  CheckCircle,
  Cancel,
  Assignment,
  HandymanOutlined,
  AccountBox,
  Save,
  CloudUpload,
  Calculate,
  LocalOffer
} from '@mui/icons-material';
import {
  Budget,
  BudgetVersion,
  BudgetVersionCreate,
  BudgetVersionFullSave,
  StalePriceMismatch,
  Product,
  PriceList,
  Comment as PresupuestoComment,
  VersionSectionSummary,
  FreightRate,
  ErpSubmitResult
} from '../../types';
import { CommentThread } from './CommentThread';
import CalculadoraHModal from '../calculadora/CalculadoraHModal';
import { BudgetApiService } from '../../services/budgetApiService';
import { apiService } from '../../services/apiService';
import {
  stagedInstallationHasData,
  normalizeStagedInstallation,
  computeEffectiveInstallationEnvironments,
  stagedFreightHasData,
  computeEffectiveFreights,
} from '../../utils/stagedRows';
import { removeInstallationByEnvRef } from '../../utils/environmentMutations';
import { applyAmbienteDiscount, clampLineDiscount, initialLineDiscount, isOutletLine } from '../../utils/materialDiscount';
import {
  SITUACION_IVA_OPTIONS,
  SITUACION_IIBB_OPTIONS,
  SITUACION_GANANCIAS_OPTIONS,
  PROVINCIA_OPTIONS,
  CONDICION_VENTA_DEFAULT,
} from '../../constants/bejermanCatalogos';

// Constante para el ambiente de instalaciones generales (oculto en materiales)
const GENERAL_INSTALLATIONS_ENV_NAME = '__INSTALACIONES_GENERALES__';
const GENERAL_INSTALLATIONS_INDEX = -1;

interface EnvironmentFormData {
  id?: number;
  nombre: string;
  descripcion: string;
  orden: number;
  descuento_materiales?: number;  // % "Aplicar descuento" del ambiente (líneas no-outlet)
  descuentoTildado?: boolean;     // UI-only: checkbox tildado mientras el % está vacío
  materials: MaterialFormData[];
  installations: InstallationFormData[];
}

interface MaterialFormData {
  id?: number;
  // Producto existente o nuevo
  product_id?: number;
  product?: Product;
  es_in_situ: boolean;
  
  // Datos del material
  descripcion: string;
  codigo: string;
  unidades: number;
  precio_unitario: number;
  currency: 'USD' | 'ARS';
  
  // Datos de cálculo
  metros_cuadrados?: number;
  cantidad_calculada?: number;
  cantidad_manual?: number;
  usar_cantidad_manual?: boolean;
  
  // Opciones de producto
  con_desperdicio?: boolean;
  con_estructura?: boolean;

  // Descuentos de la línea (CLAUDE.md §8.7 outlet model), fracciones.
  // `descuento_producto` lo captura el servidor (sólo outlet);
  // `descuento_linea` es el aplicado - ver utils/materialDiscount.
  descuento_producto?: number;
  descuento_linea?: number;

  // Observaciones
  observaciones?: string;
}

interface InstallationFormData {
  id?: number;
  installation_service_id?: number;
  installation_service?: any; // InstallationService type, import if available
  // Solo 'custom' tiene sentido ahora (CLAUDE.md §7, decidido 2026-08-30) -
  // installation_service_id ya identifica la variante exacta.
  tipo_instalacion?: string;
  cantidad_manual?: number;
  trabajo?: string;
  descripcion_trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;  // Unidad para instalaciones custom
  // Sin currency propio (CLAUDE.md §8.5, restaurado 2026-09-01): catálogo
  // usa installation_service.currency; 'custom' sigue siendo siempre USD.
}

interface FreightFormData {
  id?: number;
  tipo_envio: string;
  descripcion_detalle?: string;
  monto: number;
  tipo: 'fijo' | 'porcentaje';
  currency: 'USD' | 'ARS';
  // CLAUDE.md §6: cuando está tildado, este Flete se cobra $0 al cliente (el
  // PDF imprime "Bonificado") pero `monto` conserva el valor real calculado.
  bonificado?: boolean;
  // Link back to the FreightRate catalog row this line was built from
  // (CLAUDE.md §8.5/§7). `codigo` is copied server-side, not settable here -
  // kept only for local display. Both undefined for a manual entry.
  freight_rate_id?: number;
  codigo?: string;
  freight_rate?: FreightRate;
}

interface BudgetDetailData {
  // Datos del presupuesto
  id: number;
  proyecto: string;
  cliente: string;
  responsable: string;
  email_cliente: string;
  tel_cliente: string;
  status: string;
  code: string;
  
  // Datos de la versión actual
  currentVersion: BudgetVersion | null;
  versions: BudgetVersion[];
  
  // Datos de ambientes, fletes, etc. (de la versión actual)
  environments: EnvironmentFormData[];
  freights: FreightFormData[];
  descuento_instalacion: number;
  descuento_fletes: number;
}

const BudgetDetailNew: React.FC = () => {
  // Estados para navegación y dirty state
  const [isDirty, setIsDirty] = useState(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Tope de descuento del rep para la lista de precios de esta versión
  // (CLAUDE.md §4/§8.11, item H). Sólo advertencia visual: el backend permite
  // el envío y lo desvía a pending_approval si se excede. null = sin tope o
  // sin lista -> no se advierte.
  const getMaterialesDiscountCap = (priceListId?: number): number | null => {
    if (!priceListId || !user?.price_list_assignments) return null;
    const assignment = user.price_list_assignments.find(
      a => a.price_list_id === priceListId
    );
    const cap = assignment?.max_discount_pct;
    return cap === undefined || cap === null ? null : Number(cap);
  };

  // Estados principales de datos y carga
  const [formData, setFormData] = useState<BudgetDetailData>({
    id: 0,
    proyecto: '',
    cliente: '',
    responsable: '',
    email_cliente: '',
    tel_cliente: '',
    status: 'draft',
    code: '',
    currentVersion: null,
    versions: [],
    environments: [],
    freights: [],
    descuento_instalacion: 0,
    descuento_fletes: 0
  });

  // Solo lectura total si la versión está vendida
  const isReadOnly = formData.currentVersion?.estado === 'sold';
  // Una versión ya enviada al cliente es inmutable (CLAUDE.md §5): para
  // cambiarla hay que generar una versión nueva, no editarla en el lugar.
  // 'lost' entra acá también - es una versión que salió y no se ganó.
  const isFieldsReadOnly = ['sent', 'sold', 'lost'].includes(
    formData.currentVersion?.estado || ''
  );

  // Espeja el grafo de transiciones del backend
  // (backend/src/services/budget_state_machine.py). El backend sigue siendo
  // quien decide - esto solo evita ofrecer opciones que va a rechazar.
  // 'sent' desde 'draft' no está acá a propósito: enviar se hace generando el
  // PDF, que es lo que corre la puerta completa.
  const versionEstado = formData.currentVersion?.estado || 'draft';
  const isAdmin = user?.role === 'admin';

  // Comentarios (CLAUDE.md §10 field report #11): crear/editar/eliminar solo
  // mientras la versión sea content-editable - mismo conjunto de estados que
  // isFieldsReadOnly ya usa (draft/pending_approval/approved), invertido.
  // No pasan por el flujo de "Guardar cambios": cada operación pega
  // directo al backend y ya queda persistida, así que solo hace falta
  // reflejar la respuesta en el estado local.
  const commentsEditable = !isFieldsReadOnly;
  const versionComments = formData.currentVersion?.comments || [];
  const updateComments = (comments: PresupuestoComment[]) => {
    setFormData(prev => ({
      ...prev,
      currentVersion: prev.currentVersion ? { ...prev.currentVersion, comments } : null,
    }));
  };
  const allowedStatusTargets = (() => {
    switch (versionEstado) {
      case 'draft':
        // Enviar (draft -> sent): corre la puerta completa (precios, fecha,
        // lock de FX, encolado de PDF). Item E: ahora el envío se dispara
        // desde el dropdown de Estado, no desde "Descargar PDF".
        return ['sent'];
      case 'pending_approval':
        // Rechazar (-> rejected, "No Aprobado") reemplaza el viejo devolver a
        // draft (CLAUDE.md §5). Requiere motivo - ver reasonDialog.
        return isAdmin ? ['approved', 'rejected'] : [];
      case 'rejected':
        // "No Aprobado" se comporta como draft: el vendedor edita y reenvía.
        return ['sent'];
      case 'approved':
        return ['sent'];
      case 'sent':
        // 'draft' acá spawnea una versión nueva; no modifica esta.
        return ['sold', 'lost', 'draft'];
      case 'sold':
      case 'lost':
        return isAdmin ? ['sent'] : [];
      default:
        return [];
    }
  })();

  // Item E: opciones que ofrece el dropdown de Estado. Igual a
  // allowedStatusTargets, pero SIN el sent->draft: eso no cambia el estado de
  // esta versión, spawnea una N+1, y ya tiene su propio botón "Crear Nueva
  // Versión" (CLAUDE.md §5) - ofrecerlo también acá sería redundante.
  const statusDropdownTargets = allowedStatusTargets.filter(
    t => !(versionEstado === 'sent' && t === 'draft')
  );
  // Item E: el PDF sólo existe una vez enviado; "Crear Nueva Versión" sólo
  // tiene sentido a partir de una versión ya enviada (CLAUDE.md §5).
  const isSentLike = ['sent', 'sold', 'lost'].includes(versionEstado);

  // Botones de nueva versión solo visibles si el presupuesto NO está vendido
  const showVersionActions = formData.status !== 'sold';
  // Admin puede cambiar estado aunque el presupuesto esté vendido
  const canChangeStatus = showVersionActions || user?.role === 'admin';

  // Handler genérico para cambios de descuento
  const handleChangeDescuento = (field: 'descuento_instalacion' | 'descuento_fletes') => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) value = 0;
    if (value > 100) value = 100;
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };
  const [loading, setLoading] = useState(true);
  // formData and loading are declared later, only declare once
  useEffect(() => {
    // No marcar dirty al cargar datos, solo por acciones del usuario
  }, []);

  // Resetear dirty cuando se guarda exitosamente
  const handleAfterSave = () => setIsDirty(false);

  // Guarda contra pérdida de cambios sin guardar (CLAUDE.md §10 report #2/#4).
  // Mismo mecanismo que la pantalla de creación (BudgetFormNew): un window.confirm
  // ante cualquier navegación interna (`pushState`) o cierre/refresh de pestaña
  // (`beforeunload`) cuando isDirty. NUNCA autoguarda: el guardado es siempre
  // explícito vía el botón "Actualizar Presupuesto". Reemplaza el viejo blocker
  // roto de React Router v6 (navigate((tx)=>...) no existe en v6 con
  // BrowserRouter) y el autoguardado silencioso al navegar/periódico.
  useUnsavedChangesGuard(isDirty);

  // Estados para productos
  const [products, setProducts] = useState<Product[]>([]);

  // Listas de precios asignadas al rep (CLAUDE.md §4) y el mapa de precios
  // {product_id: price} de la lista elegida en esta versión - null = sin
  // lista elegida, mismo skip que aplica el backend en ese caso.
  const [myPriceLists, setMyPriceLists] = useState<PriceList[]>([]);
  const [priceMap, setPriceMap] = useState<Record<number, { price: number; descuento: number }> | null>(null);

  // Estados principales
  const [budget, setBudget] = useState<Budget | null>(null);
  const [versions, setVersions] = useState<BudgetVersion[]>([]);
  const [currentVersion, setCurrentVersion] = useState<BudgetVersion | null>(null);
  // Fuente única de Materiales+IVA / Mano de Obra convertida / Flete neto de
  // Bonificado (CLAUDE.md §6/§10 field report #6) - GET .../summary, la
  // misma llamada que ya usa CalculadoraH.tsx. Reemplaza los helpers
  // hand-rolled getTotalMaterialsWithDiscounts/getTotalInstallationsARS/
  // getTotalFreights que existían más abajo en este archivo.
  const [versionSummary, setVersionSummary] = useState<VersionSectionSummary | null>(null);
  // (removed duplicate formData declaration)

  // Estados de UI
  // (removed duplicate loading declaration)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCondiciones, setShowCondiciones] = useState(false);
  
  // Estados para diálogos
  const [environmentDialog, setEnvironmentDialog] = useState(false);
  const [materialDialog, setMaterialDialog] = useState(false);
  const [freightDialog, setFreightDialog] = useState(false);
  const [installationDialog, setInstallationDialog] = useState(false);
  const [newVersionDialog, setNewVersionDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  
  // Estados para formularios de diálogos
  const [selectedEnvironmentIndex, setSelectedEnvironmentIndex] = useState<number | null>(null);
  const [newEnvironment, setNewEnvironment] = useState<EnvironmentFormData>({
    nombre: '',
    descripcion: '',
    orden: 1,
    materials: [],
    installations: []
  });
  const [newMaterial, setNewMaterial] = useState<MaterialFormData>({
    descripcion: '',
    codigo: '',
    unidades: 0,
    precio_unitario: 0,
    currency: 'USD',
    es_in_situ: false,
    metros_cuadrados: 1,
    usar_cantidad_manual: false,
    con_desperdicio: true,
    con_estructura: false,
    observaciones: ''
  });
  const [newFreight, setNewFreight] = useState<FreightFormData>({
    tipo_envio: '',
    monto: 0,
    tipo: 'fijo',
    currency: 'ARS',
    bonificado: false
  });
  // Catálogo real de Fletes (CLAUDE.md §6/§7/§8.5: zona x tramo de
  // capacidad) - antes el diálogo de Flete no lo consultaba en absoluto.
  const [freightRates, setFreightRates] = useState<FreightRate[]>([]);
  const [loadingFreightRates, setLoadingFreightRates] = useState(false);
  const [freightMode, setFreightMode] = useState<'catalog' | 'manual'>('catalog');
  const [installationServices, setInstallationServices] = useState<any[]>([]);
  const [loadingInstallationServices, setLoadingInstallationServices] = useState(false);
  const [newInstallation, setNewInstallation] = useState<InstallationFormData>({
    cantidad_manual: 1
  });
  const [installationMode, setInstallationMode] = useState<'catalog' | 'custom'>('catalog');
  const [alertStock, setAlertStock] = useState<string | null>(null);
  const [showAlertStock, setShowAlertStock] = useState(false);

  // Edición en línea de ítems ya cargados (Materiales/MO/Flete) - reabre la
  // misma fila inline de alta, precargada, en vez de un flujo separado
  // (Phase 8.7 Round A, item 2).
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<{ envIndex: number; matIndex: number } | null>(null);
  const [editingInstallationIndex, setEditingInstallationIndex] = useState<{ envIndex: number; instIndex: number } | null>(null);
  const [editingFreightIndex, setEditingFreightIndex] = useState<number | null>(null);
  
  // Etiqueta editable de la versión vigente (field report #16) - solo
  // estado local, viaja al backend con el próximo guardado completo
  // (buildFullSavePayload ya incluye nombre_version), sin botón de guardado
  // aparte.
  const [editingVersionName, setEditingVersionName] = useState(false);
  const [editingVersionNameValue, setEditingVersionNameValue] = useState('');

  // Estado para edición de nombres de ambientes
  const [editingEnvironmentId, setEditingEnvironmentId] = useState<number | null>(null);
  const [editingEnvironmentName, setEditingEnvironmentName] = useState<string>('');

  // Edición del nombre de versión (field report #16) desde la tabla de
  // Historial de Versiones - a diferencia del header (que edita la versión
  // vigente y viaja con el próximo guardado completo), acá se puede editar
  // CUALQUIER versión de la lista, incluidas las viejas/inmutables que
  // nunca van a pasar por buildFullSavePayload - por eso pega directo al
  // PUT liviano /budgets/{id}/versions/{id} (BudgetVersionUpdate ya acepta
  // nombre_version sin pasar por la máquina de estados).
  const [editingVersionListId, setEditingVersionListId] = useState<number | null>(null);
  const [editingVersionListName, setEditingVersionListName] = useState<string>('');
  const [savingVersionListName, setSavingVersionListName] = useState(false);
  const [savingEnvironmentName, setSavingEnvironmentName] = useState(false);



  // Cargar installation services al inicio
  useEffect(() => {
    loadInstallationServices();
    loadFreightRates();
  }, []);

  // Catálogo real de Fletes (zona x tramo de capacidad) para el picker de la
  // sección Flete - CLAUDE.md §6/§7: "Flete → structural lookup (zone x
  // capacity tier) + manual entries". Solo tarifas activas.
  const loadFreightRates = async () => {
    try {
      setLoadingFreightRates(true);
      const rates = await apiService.freightRates.getAll(true);
      setFreightRates(Array.isArray(rates) ? rates : []);
    } catch (error) {
      setError('Error al cargar tarifas de flete');
    } finally {
      setLoadingFreightRates(false);
    }
  };

  const loadInstallationServices = async () => {
    try {
      setLoadingInstallationServices(true);
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
  const response = await fetch(`/api/installation-services?is_active=true&limit=100`, {
        method: 'GET',
        headers
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const services = await response.json();
      setInstallationServices(Array.isArray(services) ? services : []);
    } catch (error) {
      setError('Error al cargar servicios de instalación');
    } finally {
      setLoadingInstallationServices(false);
    }
  };

  // Handler para seleccionar servicio de instalación
  const handleInstallationServiceSelect = (service: any | null) => {
    if (!service) {
      setNewInstallation(prev => ({
        ...prev,
        installation_service_id: undefined,
        installation_service: undefined,
        tipo_instalacion: undefined,
        trabajo: undefined
      }));
      return;
    }

    // Calcular área del ambiente si hay uno seleccionado y la unidad es m²
    let cantidadInicial = 1;
    if (selectedEnvironmentIndex !== null && formData.environments[selectedEnvironmentIndex]) {
      const unidad = service.unidad?.toLowerCase() || '';
      if (unidad === 'm²' || unidad === 'm2' || unidad === 'm2') {
        const areaTotal = formData.environments[selectedEnvironmentIndex].materials.reduce(
          (total, mat) => total + (mat.metros_cuadrados || 0), 0
        );
        if (areaTotal > 0) {
          cantidadInicial = Math.round(areaTotal * 100) / 100; // Redondear a 2 decimales
        }
      }
    }

    setNewInstallation(prev => ({
      ...prev,
      installation_service_id: service.id,
      installation_service: service,
      // `trabajo` se retiró del catálogo (CLAUDE.md §7, decidido
      // 2026-09-03) - `descripcion` es ahora el único nombre.
      trabajo: service.descripcion || '',
      cantidad_manual: cantidadInicial
    }));
  };

  // Calcular precio (USD, siempre) del servicio seleccionado - cada
  // variante (Standard/Compleja) es su propia fila del catálogo con su
  // propio `precio` (CLAUDE.md §7, decidido 2026-08-30), sin rama por tipo.
  const calculateInstallationPrice = (): number => {
    // Si el usuario editó el precio (mano_obra_m2), usarlo directamente
    if (newInstallation.mano_obra_m2) {
      return newInstallation.mano_obra_m2;
    }

    return newInstallation.installation_service?.precio || 0;
  };

  // Formatear moneda específica para installation services
  const formatInstallationCurrency = (amount: number, currency: string) => {
    if (currency === 'USD') {
      return `U$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `AR$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };
  const [newVersionData, setNewVersionData] = useState<Partial<BudgetVersionCreate>>({
    titulo: '',
    vendedor: '',
    email_vendedor: '',
    tel_vendedor: '',
    condiciones_contratacion: '',
    numero_version: '',
    nombre: '',
    condiciones: ''
  });

  // Estados para manejo de versiones
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  // Prompt bloqueante de fecha prevista de entrega (CLAUDE.md §5). Se abre
  // cuando el backend contesta 422 delivery_date_required, precargado con lo
  // que él sugiere (la fecha de la versión anterior, en el caso de una versión
  // spawneada), y al confirmar reintenta la MISMA transición con la fecha.
  const [deliveryPrompt, setDeliveryPrompt] = useState<{
    open: boolean; target: string; value: string; afterPdf: boolean;
    // % Facturado (100 - H) que el rep confirma junto con la fecha al vender
    // (CLAUDE.md §5, PO 2026-09-15). Sólo se usa cuando target === 'sold'.
    porcentajeFacturado: number;
  }>({ open: false, target: '', value: '', afterPdf: false, porcentajeFacturado: 50 });

  // % H editable en la pantalla del presupuesto (CLAUDE.md §5, PO 2026-09-15).
  // Se maneja en términos de "% Facturado" (100 - porcentaje_h), igual que la
  // Calculadora - lo que el rep piensa. `hModalOpen` abre el popup con el split.
  const [facturadoInput, setFacturadoInput] = useState<number>(50);
  const [hModalOpen, setHModalOpen] = useState(false);
  // Sincronizar el input de % Facturado con el H persistido cada vez que cambia
  // la versión vigente (carga, cambio de versión, guardado del H).
  const currentPorcentajeH = formData.currentVersion?.porcentaje_h;
  useEffect(() => {
    setFacturadoInput(
      currentPorcentajeH != null ? 100 - Number(currentPorcentajeH) : 50
    );
  }, [currentPorcentajeH, formData.currentVersion?.id]);
  // Diálogo de rechazo (CLAUDE.md §5): el admin rechaza una versión pendiente y
  // DEBE escribir un motivo, que se guarda y se muestra después (tooltip + banner).
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; reason: string }>(
    { open: false, reason: '' }
  );
  const [staleReport, setStaleReport] = useState<StalePriceMismatch[]>([]);

  // "Enviar a ERP" (CLAUDE.md §8.1-§8.12). Un solo diálogo hace: elegir secciones,
  // buscar/crear cliente y enviar. `result` guarda la última respuesta del submit
  // (maneja tanto ok:true como ok:false/parcial). La búsqueda devuelve registros
  // nativos crudos de Bejerman (claves Cliente_*).
  const [erpDialog, setErpDialog] = useState(false);
  const [erpSections, setErpSections] = useState({
    materiales: true, mano_obra: false, flete: false,
  });
  const [erpClienteQuery, setErpClienteQuery] = useState('');
  const [erpSearching, setErpSearching] = useState(false);
  const [erpMatches, setErpMatches] = useState<any[]>([]);
  const [erpSelectedCliente, setErpSelectedCliente] = useState<any | null>(null);
  const [erpShowCreate, setErpShowCreate] = useState(false);
  // Defaults VEC conocidos-buenos (SUPERVISOR_NOTES / muestra de cliente live),
  // prefill editable.
  const [erpCreateForm, setErpCreateForm] = useState({
    razon_social: '', cuit: '',
    situacion_iva: '1', situacion_iibb: '1', situacion_ganancias: '1',
    codigo_provincia: '002', vendedor_codigo: '47', condicion_venta_codigo: CONDICION_VENTA_DEFAULT,
  });
  const [erpSubmitting, setErpSubmitting] = useState(false);
  const [erpResult, setErpResult] = useState<ErpSubmitResult | null>(null);
  const [erpError, setErpError] = useState<string | null>(null);
  // El envío ahora corre en background (RQ). Si el poll no resuelve dentro de la
  // ventana (arranque en frío del gateway), mostramos "procesando" y el rep puede
  // irse; la superficie pasiva del presupuesto muestra el resultado al terminar.
  const [erpProcessing, setErpProcessing] = useState(false);

  // Cargar datos al inicio
  useEffect(() => {
    if (id) {
      loadBudgetData();
      loadProducts();
      loadMyPriceLists();
    }
  }, [id]);

  // Listas de precios asignadas al rep, en el país activo (CLAUDE.md §4).
  const loadMyPriceLists = async () => {
    try {
      const lists = await apiService.priceLists.getMine();
      setMyPriceLists(lists);
    } catch (error) {
      console.error('Error loading price lists:', error);
    }
  };

  // Mapa {product_id: price} de la lista elegida en la versión actual - lo
  // que el picker de Materiales usa para gatear/priciar (ver
  // handleProductSelect e isProductGated). Sin lista elegida el mapa queda
  // en null y el picker no gatea nada (mismo skip que aplica el backend).
  useEffect(() => {
    const priceListId = formData.currentVersion?.price_list_id;
    if (!priceListId) {
      setPriceMap(null);
      return;
    }
    let cancelled = false;
    apiService.priceLists.getPrices(priceListId)
      .then(map => {
        if (!cancelled) setPriceMap(map);
      })
      .catch(error => {
        console.error('Error loading price list prices:', error);
        if (!cancelled) setPriceMap(null);
      });
    return () => { cancelled = true; };
  }, [formData.currentVersion?.price_list_id]);

  // CLAUDE.md §4: "falta precio en la lista elegida -> bloquear, sin
  // fallback a otra lista". Sin lista elegida (priceMap === null) no se
  // gatea nada - mismo skip que el backend aplica para versiones sin
  // price_list_id.
  const isProductGated = (product: Product): boolean => {
    return priceMap !== null && !(product.id in priceMap);
  };

  // Efecto para manejar la alerta de stock
  useEffect(() => {
    if (alertStock) {
      setShowAlertStock(true);
      const timer = setTimeout(() => {
        setShowAlertStock(false);
        setAlertStock(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alertStock]);

  const loadProducts = async () => {
    try {
      const productsData = await apiService.products.getAll({ active_only: true, limit: 1000 });
      setProducts(Array.isArray(productsData) ? productsData : productsData.items || []);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const loadBudgetData = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const [budgetData, versionsData] = await Promise.all([
        BudgetApiService.getBudget(parseInt(id)),
        BudgetApiService.getBudgetVersions(parseInt(id))
      ]);
      
      setBudget(budgetData);
      
      // Seleccionar siempre la última versión (mayor version_number)
      // El backend devuelve las versiones ordenadas por version_number DESC
      const currentVersionData: BudgetVersion | null = versionsData.length > 0 ? versionsData[0] : null;
      
      // Marcar la versión actual en el array
      const versionsWithCurrent = versionsData.map(v => ({
        ...v,
        is_current: currentVersionData ? v.id === currentVersionData.id : false
      }));
      setVersions(versionsWithCurrent);

      if (currentVersionData) {
        try {
          const versionWithDetails = await BudgetApiService.getBudgetVersion(parseInt(id), currentVersionData.id);
          setCurrentVersion(versionWithDetails);
          
          // Enriquecer materiales con información completa del producto
          const enrichedEnvironments = await Promise.all(
            (versionWithDetails.environments || []).map(async (env: any) => ({
              ...env,
              materials: await Promise.all(
                (env.materials || []).map(async (mat: any) => {
                  // Si el material tiene product_id, cargar información completa del producto
                  if (mat.product_id && !mat.es_in_situ) {
                    try {
                      const productInfo = await apiService.products.getById(mat.product_id);
                      return {
                        ...mat,
                        product: productInfo
                      };
                    } catch (err) {
                      console.warn(`Error loading product ${mat.product_id}:`, err);
                      return mat;
                    }
                  }
                  return mat;
                })
              )
            }))
          );
          
          // Mapear los datos a la estructura del formulario
          setFormData({
            id: budgetData.id,
            proyecto: budgetData.proyecto,
            cliente: budgetData.cliente,
            responsable: budgetData.responsable || '',
            email_cliente: budgetData.email_cliente || '',
            tel_cliente: budgetData.tel_cliente || '',
            status: budgetData.status,
            code: budgetData.code,
            currentVersion: versionWithDetails,
            versions: versionsData,
            environments: mapEnvironmentsToFormData(enrichedEnvironments),
            freights: mapFreightsToFormData(versionWithDetails.freights || []),
            descuento_instalacion: (versionWithDetails as any).descuento_instalacion ?? 0,
            descuento_fletes: (versionWithDetails as any).descuento_fletes ?? 0
          });
        } catch (versionError) {
          console.error('Error loading version details:', versionError);
        }
      }
    } catch (err: any) {
      console.error('Error loading budget:', err);
      setError(err.response?.data?.detail || 'Error al cargar el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  // Resumen por sección canónico (CLAUDE.md §6/§10 field report #6) - se
  // vuelve a pedir cada vez que cambia la versión vigente, y también después
  // de cada guardado exitoso (updateAllBudgetData), ya que un guardado puede
  // cambiar los totales sin cambiar el id de la versión.
  const refreshVersionSummary = async (versionId: number) => {
    if (!id) return;
    try {
      const summary = await BudgetApiService.getVersionSummary(parseInt(id), versionId);
      setVersionSummary(summary);
    } catch (err) {
      console.error('Error loading version summary:', err);
    }
  };

  useEffect(() => {
    if (currentVersion?.id) {
      refreshVersionSummary(currentVersion.id);
    } else {
      setVersionSummary(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVersion?.id]);

  // Función para mapear datos de ambientes a estructura del formulario
  const mapEnvironmentsToFormData = (environments: any[]): EnvironmentFormData[] => {
    return environments.map(env => ({
      id: env.id,
      nombre: env.nombre,
      descripcion: env.descripcion || '',
      orden: env.orden,
      descuento_materiales: env.descuento_materiales || 0,
      materials: (env.materials || []).map((mat: any) => ({
        id: mat.id,
        descripcion: mat.descripcion,
        codigo: mat.codigo,
        unidades: mat.unidades,
        precio_unitario: mat.precio_unitario,
        currency: mat.currency || 'USD',
        es_in_situ: mat.es_in_situ || false,
        product_id: mat.product_id,
        product: mat.product, // Incluir información del producto
        observaciones: mat.observaciones || '',
        metros_cuadrados: mat.metros2 || 0,
        con_desperdicio: mat.con_desperdicio || false, // Agregar flag de desperdicio
        con_estructura: mat.con_estructura || false, // Agregar flag de estructura
        // Descuentos de la línea (CLAUDE.md §8.7 outlet model) -
        // `descuento_producto` lo gestiona el servidor (read-only en UI).
        descuento_producto: Number(mat.descuento_producto ?? 0),
        descuento_linea: Number(mat.descuento_linea ?? 0)
      })),
      installations: (env.installations || []).map((inst: any) => ({
        id: inst.id,
        trabajo: inst.trabajo || inst.descripcion, // Compatibilidad con datos antiguos
        descripcion_trabajo: inst.descripcion_trabajo,
        mano_obra_m2: inst.mano_obra_m2,
        // Sin currency propio en esta línea - installation_service (abajo)
        // ya trae su propia `currency` (CLAUDE.md §8.5, restaurado 2026-09-01).
        installation_service_id: inst.installation_service_id,
        installation_service: inst.installation_service,
        tipo_instalacion: inst.tipo_instalacion,
        cantidad_manual: inst.cantidad_manual,
        unidad: inst.unidad
      }))
    }));
  };

  // Función para mapear datos de fletes a estructura del formulario
  const mapFreightsToFormData = (freights: any[]): FreightFormData[] => {
    return freights.map(freight => ({
      id: freight.id,
      tipo_envio: freight.tipo_envio || freight.descripcion || '', // Compatibilidad durante migración
      descripcion_detalle: freight.descripcion_detalle,
      monto: freight.monto,
      tipo: freight.tipo || 'fijo',
      currency: freight.currency || 'ARS',
      bonificado: freight.bonificado || false,
      // Preserve the FreightRate FK/codigo already on the saved line
      // (CLAUDE.md §8.5) - otherwise the next full-save would silently drop
      // it for any freight that already existed before this edit session.
      freight_rate_id: freight.freight_rate_id,
      codigo: freight.codigo
    }));
  };

  // Funciones de cálculo (copiadas de BudgetFormNew)
  // Reemplazado 2026-09-04 (Product Owner) - misma lógica que
  // BudgetFormNew.tsx, ver ese archivo para el razonamiento completo: la
  // vieja lógica de largo × ancho_util queda retirada por completo, no como
  // fallback. `factor_conversion_m2` (CLAUDE.md §7) es un factor de
  // unidades por m²: `cantidad = m² × factor`.
  const calculateMaterialQuantity = (product: Product, metros_cuadrados: number, con_desperdicio: boolean = false): number => {
    if (!product.factor_conversion_m2) return 1;

    let cantidad = Math.ceil(metros_cuadrados * product.factor_conversion_m2);

    if (con_desperdicio) {
      cantidad = Math.ceil(cantidad * 1.1);
    }

    return cantidad;
  };

  // Inversa de calculateMaterialQuantity, mismo factor_conversion_m2.
  const calculateMetrosCuadradosFromQuantity = (product: Product, cantidad: number): number => {
    if (!product.factor_conversion_m2 || cantidad <= 0) return 0;

    return cantidad / product.factor_conversion_m2;
  };

  // Funciones de manejo de productos (copiadas de BudgetFormNew)
  const handleProductSelect = (product: Product | null) => {
    if (!product) {
      setNewMaterial(prev => ({
        ...prev,
        product_id: undefined,
        product: undefined,
        descripcion: '',
        codigo: '',
        precio_unitario: 0,
        cantidad_calculada: 0
      }));
      return;
    }

    // Defensa además del getOptionDisabled del Autocomplete (CLAUDE.md §4:
    // bloquear sin excepción un producto sin precio en la lista elegida).
    if (isProductGated(product)) {
      return;
    }

    const cantidadCalculada = calculateMaterialQuantity(
      product,
      newMaterial.metros_cuadrados || 1,
      newMaterial.con_desperdicio || false
    );

    // Precio: si hay una lista elegida, sale del mapa de esa lista - Product
    // ya no tiene un precio de catálogo propio al que recurrir (CLAUDE.md
    // §4). El backend vuelve a exigir/resolver esto de forma independiente
    // (defense in depth, incluyendo el fallback a "General Público"); esto
    // es solo para que el resumen que ve el rep muestre un número mientras
    // tanto, no la fuente de verdad.
    const entry = priceMap !== null ? priceMap[product.id] : undefined;
    const precioUnitario = entry?.price ?? 0;
    // CLAUDE.md §8.7 outlet model: sólo un producto outlet trae descuento de
    // producto (fracción, de la entrada de la lista); no-outlet captura 0.
    const descuentoProducto = product.is_outlet ? (entry?.descuento ?? 0) : 0;

    setNewMaterial(prev => ({
      ...prev,
      product_id: product.id,
      product: product,
      descripcion: product.codigo,
      codigo: product.codigo,
      precio_unitario: precioUnitario,
      descuento_producto: descuentoProducto,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  const handleMetrosCuadradosChange = (metros: number) => {
    if (!newMaterial.product) return;
    
    const cantidadCalculada = calculateMaterialQuantity(
      newMaterial.product, 
      metros, 
      newMaterial.con_desperdicio || false
    );
    
    setNewMaterial(prev => ({
      ...prev,
      metros_cuadrados: metros,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  const handleCantidadManualChange = (cantidad: number) => {
    if (!newMaterial.product) return;
    
    let cantidadBase = cantidad;
    
    if (newMaterial.con_desperdicio) {
      cantidadBase = Math.ceil(cantidad / 1.1);
    }
    
    const metrosCalculados = calculateMetrosCuadradosFromQuantity(newMaterial.product, cantidadBase);
    
    setNewMaterial(prev => ({
      ...prev,
      cantidad_manual: cantidad,
      unidades: cantidad,
      metros_cuadrados: Math.round(metrosCalculados)
    }));
  };

  const handleDesperdicioChange = (con_desperdicio: boolean) => {
    if (!newMaterial.product || !newMaterial.metros_cuadrados) return;
    
    const cantidadCalculada = calculateMaterialQuantity(
      newMaterial.product, 
      newMaterial.metros_cuadrados, 
      con_desperdicio
    );
    
    setNewMaterial(prev => ({
      ...prev,
      con_desperdicio,
      cantidad_calculada: cantidadCalculada,
      unidades: prev.usar_cantidad_manual ? (prev.cantidad_manual || 0) : cantidadCalculada
    }));
  };

  // Funciones de manejo de ambientes
  const handleAddEnvironment = () => {
    if (!newEnvironment.nombre.trim()) return;

    setFormData(prev => ({
      ...prev,
      environments: [...prev.environments, { 
        ...newEnvironment, 
        orden: prev.environments.length + 1,
        materials: []
      }]
    }));

    setNewEnvironment({
      nombre: '',
      descripcion: '',
      orden: 1,
      materials: [],
      installations: []
    });
    setEnvironmentDialog(false);
  };

  const handleDeleteEnvironment = async (index: number) => {
    try {
      setSaving(true);
      const environment = formData.environments[index];

      // NOTE: this used to manually delete every material, then every
      // installation, in separate sequential requests, before finally
      // deleting the environment itself - all redundant, since
      // BudgetService.delete_environment already cascades all of that in one
      // transactional backend call. Worse, it was the actual cause of field
      // report #3 ("Eliminar a veces falla, forzando un reload"): any single
      // request failing partway through this chain (a transient network
      // error, nothing exotic) left the environment only partially deleted
      // server-side, aborted before the local state was ever updated to
      // match, and gave no way to recover except reloading the page. One
      // call is both simpler and safer than N.
      if (environment.id && environment.id > 0) {
        console.log(`🔥 Eliminando ambiente del backend con ID: ${environment.id}`);
        await BudgetApiService.deleteEnvironment(environment.id);
        console.log(`✅ Ambiente "${environment.nombre}" eliminado del backend (con materiales/instalaciones en cascada)`);
      } else {
        console.log(`⚠️ Ambiente sin ID válido, solo eliminando del estado local:`, environment);
      }

      // Eliminar del estado local
      setFormData(prev => ({
        ...prev,
        environments: prev.environments.filter((_, i) => i !== index)
      }));
      
      console.log(`✅ Ambiente eliminado del estado local`);
      
    } catch (error) {
      console.error('❌ Error eliminando ambiente:', error);
      setError('Error al eliminar el ambiente');
    } finally {
      setSaving(false);
    }
  };

  // Funciones de edición de nombre de ambiente
  const handleStartEditEnvironmentName = (envId: number, currentName: string) => {
    setEditingEnvironmentId(envId);
    setEditingEnvironmentName(currentName);
  };

  const handleCancelEditEnvironmentName = () => {
    setEditingEnvironmentId(null);
    setEditingEnvironmentName('');
  };

  const handleSaveEnvironmentName = async (envId: number) => {
    // Validar nombre no vacío
    const trimmedName = editingEnvironmentName.trim();
    if (!trimmedName) {
      setError('El nombre del ambiente no puede estar vacío');
      return;
    }

    try {
      setSavingEnvironmentName(true);
      
      // Llamada al API para actualizar el nombre
      await apiService.put(`/budgets/environments/${envId}`, {
        nombre: trimmedName
      });

      // Actualizar estado local
      setFormData(prev => ({
        ...prev,
        environments: prev.environments.map(env =>
          env.id === envId ? { ...env, nombre: trimmedName } : env
        )
      }));

      // Limpiar estado de edición
      setEditingEnvironmentId(null);
      setEditingEnvironmentName('');
      
      setSuccess('Nombre del ambiente actualizado correctamente');
    } catch (error: any) {
      console.error('Error actualizando nombre del ambiente:', error);
      setError(error.response?.data?.detail || 'Error al actualizar el nombre del ambiente');
    } finally {
      setSavingEnvironmentName(false);
    }
  };

  const handleStartEditVersionListName = (versionId: number, currentName?: string) => {
    setEditingVersionListId(versionId);
    setEditingVersionListName(currentName || '');
  };

  const handleCancelEditVersionListName = () => {
    setEditingVersionListId(null);
    setEditingVersionListName('');
  };

  const handleSaveVersionListName = async (versionId: number) => {
    if (!budget) return;
    const trimmedName = editingVersionListName.trim();
    if (!trimmedName) {
      setError('El nombre de la versión no puede estar vacío');
      return;
    }

    try {
      setSavingVersionListName(true);
      await BudgetApiService.updateBudgetVersion(budget.id, versionId, { nombre_version: trimmedName });

      // Reflejar el nombre nuevo en la lista sin recargar toda la página, y
      // si es la versión vigente, también en formData.currentVersion - así
      // el próximo guardado completo (buildFullSavePayload) no lo pisa con
      // un valor viejo.
      setVersions(prev => prev.map(v => v.id === versionId ? { ...v, nombre_version: trimmedName } : v));
      setFormData(prev => ({
        ...prev,
        currentVersion: prev.currentVersion?.id === versionId
          ? { ...prev.currentVersion, nombre_version: trimmedName }
          : prev.currentVersion,
        versions: prev.versions.map(v => v.id === versionId ? { ...v, nombre_version: trimmedName } : v)
      }));

      setEditingVersionListId(null);
      setEditingVersionListName('');
      setSuccess('Nombre de la versión actualizado correctamente');
    } catch (error: any) {
      console.error('Error actualizando nombre de la versión:', error);
      // El backend puede devolver detail como string simple o, para
      // TransitionError (p.ej. version_not_editable si esta fila quedó
      // sent/sold/lost mientras la pestaña estaba abierta), como
      // {code, message} - igual que /h-percentage (ver CalculadoraH.tsx). Un
      // objeto crudo dentro de <Alert>{error}</Alert> rompe el render.
      const detail = error.response?.data?.detail;
      setError(
        (typeof detail === 'string' ? detail : detail?.message)
        || 'Error al actualizar el nombre de la versión'
      );
    } finally {
      setSavingVersionListName(false);
    }
  };

  // Funciones de manejo de materiales
  const handleAddMaterial = async () => {
    // Bloqueo real (no solo visual, ver el botón "Agregar Material" y su
    // disabled más abajo): sin una lista de precios elegida en la versión
    // actual no hay contra qué verificar el precio de Materiales, así que no
    // se puede completar el alta - antes esto caía en un fallback silencioso
    // a "precio de catálogo sin verificar", que ya no existe (CLAUDE.md §4).
    if (!formData.currentVersion?.price_list_id) return;
    if (selectedEnvironmentIndex === null) return;

    // Verificar stock antes de agregar
    if (newMaterial.product && typeof newMaterial.product.stock === 'number') {
      const cantidadPresupuestada = newMaterial.usar_cantidad_manual 
        ? (newMaterial.cantidad_manual || 0) 
        : (newMaterial.cantidad_calculada || newMaterial.unidades);
      
      if (cantidadPresupuestada > newMaterial.product.stock) {
        setAlertStock(
          `Estás por presupuestar más de lo que hay en stock (${newMaterial.product.stock}). Revisa los próximos ingresos.`
        );
        // No hacer return, solo mostrar la alerta
      } else {
        setAlertStock(null);
      }
    }

    try {
      // Si tiene estructura, agregar también los materiales de estructura
      if (newMaterial.con_estructura && newMaterial.product?.estructura_m2) {
        await addStructureMaterials(selectedEnvironmentIndex, newMaterial.product, newMaterial.metros_cuadrados || 1);
      }

      // Agregar el material principal
      let materialToAdd = {
        ...newMaterial,
        unidades: newMaterial.usar_cantidad_manual ? (newMaterial.cantidad_manual || 0) : (newMaterial.cantidad_calculada || newMaterial.unidades)
      };
      // Si el material es de grupo ESTRUCTURA, forzar flags y observaciones igual que los auto-agregados
      if (newMaterial.product && newMaterial.product.grupo === 'ESTRUCTURA') {
        materialToAdd = {
          ...materialToAdd,
          metros_cuadrados: undefined,
          con_estructura: false,
          con_desperdicio: false,
          usar_cantidad_manual: false,
          observaciones: 'Estructura para ', // código vacío
        };
      }
      setFormData(prev => {
        const newEnvironments = [...prev.environments];
        const targetEnv = newEnvironments[selectedEnvironmentIndex];
        // Descuento inicial (CLAUDE.md §8.7): outlet -> el de producto;
        // no-outlet -> el % del ambiente (0 si no está tildado).
        targetEnv.materials.push({
          ...materialToAdd,
          descuento_linea: initialLineDiscount(materialToAdd, targetEnv.descuento_materiales || 0),
        });
        return { ...prev, environments: newEnvironments };
      });

      resetMaterialForm();
    } catch (err) {
      setError('Error al agregar el material');
    }
  };

  // Función para agregar materiales de estructura (copiada de BudgetFormNew)
  const addStructureMaterials = async (envIndex: number, product: Product, metros_cuadrados: number) => {
    if (!product.estructura_m2) return;

    const structureEntries = product.estructura_m2.split(/[;\n]+/);
    
    for (const entry of structureEntries) {
      const trimmedEntry = entry.trim();
      if (!trimmedEntry) continue;
      
      try {
        const [codigo, cantidadPorM2Str] = trimmedEntry.split(',');
        const codigoTrimmed = codigo.trim();
        const cantidadPorM2 = parseFloat(cantidadPorM2Str.trim());
        
        const structureProduct = products.find(p => p.codigo === codigoTrimmed);
        if (!structureProduct) continue;

        const cantidadBase = (newMaterial.product?.grupo === 'PERFIL')
          ? (newMaterial.unidades || 1)
          : (metros_cuadrados || 1);
        const cantidadTotal = cantidadPorM2 * cantidadBase;
        const cantidadFinal = Math.ceil(cantidadTotal);
        
        const structureMaterial: MaterialFormData = {
          product_id: structureProduct.id,
          product: structureProduct,
          es_in_situ: false,
          descripcion: structureProduct.codigo,
          codigo: structureProduct.codigo,
          unidades: cantidadFinal,
          // CLAUDE.md §4: Product ya no tiene precio propio - el backend
          // resuelve el precio real server-side al guardar.
          precio_unitario: (priceMap !== null ? priceMap[structureProduct.id]?.price : undefined) ?? 0,
          // Materiales es siempre USD, sin excepción (CLAUDE.md §6) -
          // Product ya no tiene una moneda propia (CLAUDE.md §4).
          currency: 'USD',
          metros_cuadrados: undefined, // ✅ Items de estructura NO tienen m2
          cantidad_calculada: cantidadFinal,
          usar_cantidad_manual: false,
          con_desperdicio: false,
          con_estructura: false,
          observaciones: `Estructura para ${product.codigo}`
        };
        
        setFormData(prev => {
          const newEnvironments = [...prev.environments];
          newEnvironments[envIndex].materials.push({
            ...structureMaterial,
            descuento_linea: initialLineDiscount(structureMaterial, newEnvironments[envIndex].descuento_materiales || 0),
          });
          return { ...prev, environments: newEnvironments };
        });
      } catch (error) {
        console.error('Error processing structure entry:', trimmedEntry, error);
      }
    }
  };

  const handleDeleteMaterial = async (envIndex: number, matIndex: number) => {
    try {
      setSaving(true);
      const material = formData.environments[envIndex].materials[matIndex];
      
      console.log(`🗑️ Intentando eliminar material:`, {
        envIndex,
        matIndex,
        material,
        hasId: !!material.id,
        id: material.id
      });
      
      if (material.id && material.id > 0) {
        // Eliminar del backend si tiene ID válido
        console.log(`🔥 Eliminando material del backend con ID: ${material.id}`);
        await BudgetApiService.deleteEnvironmentMaterial(material.id);
        console.log(`✅ Material "${material.descripcion}" eliminado del backend`);
      } else {
        console.log(`⚠️ Material sin ID válido, solo eliminando del estado local:`, material);
      }

      // Eliminar del estado local
      setFormData(prev => {
        const newEnvironments = [...prev.environments];
        newEnvironments[envIndex].materials = newEnvironments[envIndex].materials.filter((_, i) => i !== matIndex);
        return { ...prev, environments: newEnvironments };
      });
      
      console.log(`✅ Material eliminado del estado local`);
      
    } catch (error) {
      console.error('❌ Error eliminando material:', error);
      setError('Error al eliminar el material');
    } finally {
      setSaving(false);
    }
  };

  // Reabre una fila de material ya cargada para edición in-place (Phase 8.7
  // Round A, item 2) - misma fila/controles que el alta, precargada.
  const handleEditMaterial = (envIndex: number, matIndex: number) => {
    const material = formData.environments[envIndex].materials[matIndex];
    setNewMaterial({ ...material });
    setSelectedEnvironmentIndex(envIndex);
    setEditingMaterialIndex({ envIndex, matIndex });
    setMaterialDialog(true);
  };

  // Genera (sin mutar estado) las filas de estructura de un producto, misma
  // lógica que addStructureMaterials pero devolviendo un array para poder
  // insertarlas en un solo setFormData (round 3, item 2 - edición).
  const buildStructureMaterials = (material: MaterialFormData): MaterialFormData[] => {
    const product = material.product;
    if (!product?.estructura_m2) return [];
    const rows: MaterialFormData[] = [];
    const cantidadBase = (product.grupo === 'PERFIL')
      ? (material.unidades || 1)
      : (material.metros_cuadrados || 1);
    for (const entry of product.estructura_m2.split(/[;\n]+/)) {
      const trimmed = entry.trim();
      if (!trimmed) continue;
      try {
        const [codigo, cantidadPorM2Str] = trimmed.split(',');
        const structureProduct = products.find(p => p.codigo === codigo.trim());
        if (!structureProduct) continue;
        const cantidadFinal = Math.ceil(parseFloat(cantidadPorM2Str.trim()) * cantidadBase);
        rows.push({
          product_id: structureProduct.id,
          product: structureProduct,
          es_in_situ: false,
          descripcion: structureProduct.codigo,
          codigo: structureProduct.codigo,
          unidades: cantidadFinal,
          precio_unitario: (priceMap !== null ? priceMap[structureProduct.id]?.price : undefined) ?? 0,
          currency: 'USD',
          metros_cuadrados: undefined,
          cantidad_calculada: cantidadFinal,
          usar_cantidad_manual: false,
          con_desperdicio: false,
          con_estructura: false,
          observaciones: `Estructura para ${product.codigo}`
        });
      } catch (error) {
        console.error('Error processing structure entry:', trimmed, error);
      }
    }
    return rows;
  };

  // Recalcula las filas de estructura de la línea `matIndex` para seguir la
  // cantidad actual del padre (round 4/5, item 4). Root-cause único: reusa
  // buildStructureMaterials (misma fórmula que el alta y el toggle), quita las
  // filas de estructura viejas de este padre y reinserta las nuevas debajo.
  // Se llama desde todo punto que muta la cantidad del padre: caja inline,
  // guardado del diálogo y el toggle. Devuelve un array nuevo.
  const rebuildStructureRows = (materials: MaterialFormData[], matIndex: number): MaterialFormData[] => {
    const parent = materials[matIndex];
    const parentCodigo = parent?.product?.codigo || parent?.codigo;
    if (!parent?.con_estructura || !parent.product?.estructura_m2 || !parentCodigo) {
      return materials;
    }
    const fresh = buildStructureMaterials(parent);
    const result = materials.filter(m => m.observaciones !== `Estructura para ${parentCodigo}`);
    const parentIdx = result.indexOf(parent);
    result.splice(parentIdx + 1, 0, ...fresh);
    return result;
  };

  // Mantiene metros_cuadrados coherente con una cantidad editada a mano, para
  // que la base de la cascada refleje el cambio en productos no-PERFIL. Misma
  // inversa que handleCantidadManualChange. Devuelve el padre actualizado.
  const syncM2ToUnidades = (parent: MaterialFormData, nuevasUnidades: number): MaterialFormData => {
    const updated = { ...parent, unidades: nuevasUnidades };
    if (parent.product && !parent.es_in_situ && parent.product.grupo !== 'PERFIL' && parent.metros_cuadrados != null) {
      const base = parent.con_desperdicio ? nuevasUnidades / 1.1 : nuevasUnidades;
      const m2 = calculateMetrosCuadradosFromQuantity(parent.product, base);
      if (m2 > 0) updated.metros_cuadrados = Math.round(m2 * 100) / 100;
    }
    return updated;
  };

  // Guarda los cambios de una fila de material existente. Si al editar se
  // ACTIVA/DESACTIVA "incluir estructura" (round 3, item 2), corre la misma
  // cascada que el alta: al activar genera y agrega las filas de estructura;
  // al desactivar quita las que había generado esta línea (tagueadas por
  // observaciones "Estructura para <codigo>"). Persisten vía el guardado
  // normal: las filas nuevas van sin id (se crean server-side) y las
  // quitadas se borran en la reconciliación del bulk-save.
  const handleUpdateMaterial = () => {
    if (!editingMaterialIndex) return;
    const { envIndex, matIndex } = editingMaterialIndex;
    const updatedMaterial: MaterialFormData = {
      ...newMaterial,
      unidades: newMaterial.usar_cantidad_manual ? (newMaterial.cantidad_manual || 0) : (newMaterial.cantidad_calculada || newMaterial.unidades)
    };
    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      let materials = [...newEnvironments[envIndex].materials];
      materials[matIndex] = updatedMaterial;

      const parentCodigo = updatedMaterial.product?.codigo || updatedMaterial.codigo;
      // Estructura apagada -> quitar sus filas; encendida (recién o ya activa
      // con cantidad cambiada) -> recalcular para seguir la cantidad del padre
      // (item 4). rebuildStructureRows cubre alta y recálculo.
      if (!updatedMaterial.con_estructura && parentCodigo) {
        materials = materials.filter(m => m.observaciones !== `Estructura para ${parentCodigo}`);
      } else if (updatedMaterial.con_estructura && updatedMaterial.product?.estructura_m2 && parentCodigo) {
        materials = rebuildStructureRows(materials, matIndex);
      }

      newEnvironments[envIndex] = { ...newEnvironments[envIndex], materials };
      return { ...prev, environments: newEnvironments };
    });
    setIsDirty(true);
    setEditingMaterialIndex(null);
    resetMaterialForm();
  };

  // Funciones para manejo de instalación
  const handleAddInstallation = () => {
    if (selectedEnvironmentIndex === null) return;

    // Validar según el modo
    if (installationMode === 'catalog' && !newInstallation.installation_service_id) {
      return;
    }
    if (installationMode === 'custom' && (!newInstallation.trabajo?.trim() || !newInstallation.mano_obra_m2)) {
      return;
    }

    // Calcular el precio
    let mano_obra_m2 = newInstallation.mano_obra_m2 || 0;
    
    // Solo usar el precio del catálogo si estamos en modo catálogo y el
    // usuario no editó el campo - cada variante ya es su propia fila con su
    // propio precio (CLAUDE.md §7, decidido 2026-08-30).
    if (installationMode === 'catalog') {
      if (!newInstallation.mano_obra_m2 || newInstallation.mano_obra_m2 === 0) {
        mano_obra_m2 = newInstallation.installation_service?.precio || 0;
      }
    }

    // Calcular subtotal - para unidad global no multiplicar por cantidad
    const cantidad = newInstallation.cantidad_manual || 1;
    const esGlobal = installationMode === 'custom' && newInstallation.unidad?.toLowerCase() === 'global';
    const subtotal = esGlobal ? mano_obra_m2 : mano_obra_m2 * cantidad;

    // Preparar la instalación
    // Para custom: guardar unidad seleccionada
    // Para catálogo: NO guardar unidad (se usa la del servicio)
    const installationToAdd = {
      ...newInstallation,
      mano_obra_m2: mano_obra_m2,
      cantidad_manual: cantidad,
      subtotal: subtotal,
      unidad: installationMode === 'custom' ? (newInstallation.unidad || '') : undefined,
      tipo_instalacion: installationMode === 'custom' ? 'custom' : undefined
    };

    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      
      // Si es el ambiente general (-1), buscarlo o crearlo
      if (selectedEnvironmentIndex === GENERAL_INSTALLATIONS_INDEX) {
        let generalEnvIndex = newEnvironments.findIndex(env => env.nombre === GENERAL_INSTALLATIONS_ENV_NAME);
        
        if (generalEnvIndex === -1) {
          // Crear el ambiente general
          newEnvironments.push({
            nombre: GENERAL_INSTALLATIONS_ENV_NAME,
            descripcion: 'Instalaciones generales del presupuesto',
            orden: 9999, // Al final
            materials: [],
            installations: [installationToAdd]
          });
        } else {
          // Agregar al ambiente general existente
          newEnvironments[generalEnvIndex].installations = [
            ...newEnvironments[generalEnvIndex].installations,
            installationToAdd
          ];
        }
      } else {
        // Ambiente normal
        newEnvironments[selectedEnvironmentIndex].installations = [
          ...newEnvironments[selectedEnvironmentIndex].installations,
          installationToAdd
        ];
      }
      
      return { ...prev, environments: newEnvironments };
    });
    
    setIsDirty(true); // Marcar que hay cambios sin guardar

    // Reset the entry form for the NEXT item - deliberately do NOT close the
    // dialog or clear the selected ambiente here: a rep should be able to
    // add several MO lines to one ambiente in one sitting (direct user ask,
    // 2026-09-04). The dialog now only closes via its own explicit close
    // affordance ("Listo" / backdrop / Escape below), never automatically
    // after a successful add.
    setNewInstallation({
      trabajo: '',
      mano_obra_m2: 0,
      cantidad_manual: 1
    });
    setInstallationMode('catalog');
  };

  // Reabre una instalación ya cargada para edición in-place (Phase 8.7
  // Round A, item 2). `envIndex` es el índice REAL en formData.environments
  // (no el índice filtrado que usa la fila de borrado más abajo).
  const handleEditInstallation = (envIndex: number, instIndex: number) => {
    const installation = formData.environments[envIndex].installations[instIndex];
    setNewInstallation({ ...installation });
    setInstallationMode(installation.installation_service_id ? 'catalog' : 'custom');
    setSelectedEnvironmentIndex(envIndex);
    setEditingInstallationIndex({ envIndex, instIndex });
    setInstallationDialog(true);
  };

  const handleUpdateInstallation = () => {
    if (!editingInstallationIndex) return;
    const { envIndex, instIndex } = editingInstallationIndex;

    let mano_obra_m2 = newInstallation.mano_obra_m2 || 0;
    if (installationMode === 'catalog') {
      if (!newInstallation.mano_obra_m2 || newInstallation.mano_obra_m2 === 0) {
        mano_obra_m2 = newInstallation.installation_service?.precio || 0;
      }
    }
    const cantidad = newInstallation.cantidad_manual || 1;
    const esGlobal = installationMode === 'custom' && newInstallation.unidad?.toLowerCase() === 'global';
    const subtotal = esGlobal ? mano_obra_m2 : mano_obra_m2 * cantidad;

    const installationToUpdate = {
      ...newInstallation,
      mano_obra_m2,
      cantidad_manual: cantidad,
      subtotal,
      unidad: installationMode === 'custom' ? (newInstallation.unidad || '') : undefined,
      tipo_instalacion: installationMode === 'custom' ? 'custom' : undefined
    };

    setFormData(prev => {
      const newEnvironments = [...prev.environments];
      const installations = [...newEnvironments[envIndex].installations];
      installations[instIndex] = installationToUpdate;
      newEnvironments[envIndex] = { ...newEnvironments[envIndex], installations };
      return { ...prev, environments: newEnvironments };
    });

    setIsDirty(true);
    setEditingInstallationIndex(null);
    setInstallationDialog(false);
    setSelectedEnvironmentIndex(null);
    setNewInstallation({ trabajo: '', mano_obra_m2: 0, cantidad_manual: 1 });
    setInstallationMode('catalog');
  };

  // Deprecated: Esta función ya no se usa porque la eliminación se maneja en la tabla con onClick
  // Se mantiene solo por compatibilidad
  const handleDeleteInstallation = async (envIndex: number, instIndex: number) => {
    try {
      setSaving(true);
      const installation = formData.environments[envIndex].installations?.[instIndex];
      
      console.log(`🗑️ Intentando eliminar instalación del ambiente ${envIndex}:`, {
        envIndex,
        instIndex,
        installation,
        hasId: !!installation?.id,
        id: installation?.id
      });
      
      if (installation?.id && installation.id > 0) {
        // Eliminar del backend si tiene ID válido
        console.log(`🔥 Eliminando instalación del backend con ID: ${installation.id}`);
        await BudgetApiService.deleteEnvironmentInstallation(installation.id);
        console.log(`✅ Instalación eliminada del backend`);
      } else {
        console.log(`⚠️ Instalación sin ID válido, solo eliminando del estado local:`, installation);
      }

      // Eliminar del estado local
      setFormData(prev => {
        const newEnvironments = [...prev.environments];
        newEnvironments[envIndex].installations = newEnvironments[envIndex].installations.filter((_, idx) => idx !== instIndex);
        return { ...prev, environments: newEnvironments };
      });
      
      console.log(`✅ Instalación eliminada del estado local`);
      
    } catch (error) {
      console.error('❌ Error eliminando instalación:', error);
      setError('Error al eliminar la instalación');
    } finally {
      setSaving(false);
    }
  };

  // Funciones para manejo de fletes
  // Selección de una tarifa real del catálogo FreightRate (CLAUDE.md
  // §6/§7/§8.5) - precarga tipo_envio/monto/currency desde la fila elegida,
  // igual que handleInstallationServiceSelect hace para Mano de Obra. El
  // monto queda editable después (Flete admite edición manual de precio,
  // CLAUDE.md §6).
  const handleFreightRateSelect = (rate: FreightRate | null) => {
    if (!rate) {
      setNewFreight(prev => ({
        ...prev,
        freight_rate_id: undefined,
        freight_rate: undefined,
        codigo: undefined
      }));
      return;
    }
    setNewFreight(prev => ({
      ...prev,
      freight_rate_id: rate.id,
      freight_rate: rate,
      codigo: rate.codigo,
      tipo_envio: rate.descripcion ? `${rate.zone} - ${rate.descripcion}` : rate.zone,
      monto: Number(rate.price) || 0,
      currency: (rate.currency as 'USD' | 'ARS') || 'ARS'
    }));
  };

  const handleAddFreight = () => {
    if (freightMode === 'catalog' && !newFreight.freight_rate_id) return;
    if (freightMode === 'manual' && !newFreight.tipo_envio.trim()) return;

    setFormData(prev => ({
      ...prev,
      freights: [...prev.freights, { ...newFreight }]
    }));

    setIsDirty(true);

    // Reset the entry form for the NEXT item - deliberately do NOT close the
    // dialog here, same "stay open until explicitly closed" fix as MO above.
    setNewFreight({
      tipo_envio: '',
      monto: 0,
      tipo: 'fijo',
      currency: 'ARS',
      bonificado: false
    });
  };

  // Reabre un flete ya cargado para edición in-place (Phase 8.7 Round A,
  // item 2).
  const handleEditFreight = (index: number) => {
    const freight = formData.freights[index];
    setNewFreight({ ...freight });
    setFreightMode(freight.freight_rate_id ? 'catalog' : 'manual');
    setEditingFreightIndex(index);
    setFreightDialog(true);
  };

  const handleUpdateFreight = () => {
    if (editingFreightIndex === null) return;
    const index = editingFreightIndex;
    setFormData(prev => {
      const freights = [...prev.freights];
      freights[index] = { ...newFreight };
      return { ...prev, freights };
    });
    setIsDirty(true);
    setEditingFreightIndex(null);
    setFreightDialog(false);
    setFreightMode('catalog');
    setNewFreight({
      tipo_envio: '',
      monto: 0,
      tipo: 'fijo',
      currency: 'ARS',
      bonificado: false
    });
  };

  const handleDeleteFreight = async (index: number) => {
    try {
      setSaving(true);
      const freight = formData.freights[index];
      
      console.log(`🗑️ Intentando eliminar flete:`, {
        index,
        freight,
        hasId: !!freight.id,
        id: freight.id
      });
      
      if (freight.id && freight.id > 0) {
        // Eliminar del backend si tiene ID válido
        console.log(`🔥 Eliminando flete del backend con ID: ${freight.id}`);
        await BudgetApiService.deleteFreight(freight.id);
        console.log(`✅ Flete "${freight.tipo_envio}" eliminado del backend`);
      } else {
        console.log(`⚠️ Flete sin ID válido, solo eliminando del estado local:`, freight);
      }

      // Eliminar del estado local
      setFormData(prev => ({
        ...prev,
        freights: prev.freights.filter((_, i) => i !== index)
      }));
      
      console.log(`✅ Flete eliminado del estado local`);
      
    } catch (error) {
      console.error('❌ Error eliminando flete:', error);
      setError('Error al eliminar el flete');
    } finally {
      setSaving(false);
    }
  };

  // Función para resetear formulario de material
  const resetMaterialForm = () => {
    setNewMaterial({
      descripcion: '',
      codigo: '',
      unidades: 0,
      precio_unitario: 0,
      currency: 'USD',
      es_in_situ: false,
      metros_cuadrados: 1,
      usar_cantidad_manual: false,
      con_desperdicio: true,
      con_estructura: false,
      observaciones: ''
    });
    setSelectedEnvironmentIndex(null);
    setMaterialDialog(false);
  };

  // Funciones para crear nueva versión
  const handleCreateVersion = async () => {
    if (!formData.id || !newVersionData.numero_version) return;
    setSaving(true);
    try {
      // Buscar la versión cerrada seleccionada
      const selectedVersion = formData.versions.find(
        v => v.estado === 'sent' && String(v.version_number) === String(newVersionData.numero_version)
      );
      if (!selectedVersion) {
        setError('Debes seleccionar una versión enviada válida');
        setSaving(false);
        return;
      }
      // Guardar cambios pendientes de la versión actual antes de clonar,
      // para no perderlos cuando se recargue y se cambie a la nueva versión
      await updateAllBudgetData();
      // Clonar y obtener la nueva versión
      const newVersion = await BudgetApiService.cloneBudgetVersion(formData.id, selectedVersion.id);
      setNewVersionDialog(false);
      setNewVersionData({
        titulo: '',
        vendedor: '',
        email_vendedor: '',
        tel_vendedor: '',
        condiciones_contratacion: '',
        numero_version: '',
        nombre: '',
        condiciones: ''
      });
      setSuccess('Nueva versión creada exitosamente');
      // Recargar datos y seleccionar la nueva versión como actual
      await loadBudgetData();
      // Forzar selección de la nueva versión como actual
      setTimeout(async () => {
        try {
          const versionWithDetails = await BudgetApiService.getBudgetVersion(formData.id, newVersion.id);
          setCurrentVersion(versionWithDetails);
          setFormData(prev => ({
            ...prev,
            currentVersion: versionWithDetails
          }));
        } catch (e) {
          // Si falla, al menos los datos están recargados
        }
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al crear la versión');
    } finally {
      setSaving(false);
    }
  };

  // Función para cambiar estado del presupuesto
  /**
   * Corre una transición de estado contra el endpoint que aplica las
   * validaciones (CLAUDE.md §5). Devuelve true si la transición terminó como
   * se pidió.
   *
   * Los tres desenlaces que NO son "salió bien" y hay que tratar distinto:
   *  - 422 delivery_date_required -> abrir el prompt de fecha y reintentar
   *  - 200 con diverted_to_approval -> NO se envió: quedó pendiente de
   *    aprobación por precios desactualizados
   *  - 502 fx_unavailable -> el banco central no respondió; no se envió nada
   */
  const runTransition = async (
    target: string,
    fecha?: string,
    afterPdf = false,
    reason?: string
  ): Promise<{ ok: boolean; pdfJobId?: string }> => {
    if (!budget || !currentVersion) return { ok: false };
    try {
      const result = await BudgetApiService.transitionVersion(
        budget.id, currentVersion.id, target as any, fecha, reason
      );

      if (result.diverted_to_approval) {
        setStaleReport(result.stale_prices || []);
        const reasons: string[] = [];
        if (result.stale_prices?.length) {
          reasons.push(`${result.stale_prices.length} material(es) cambiaron de precio en la lista`);
        }
        if (result.discount_violations?.length) {
          const labels = result.discount_violations.map(v => v.section_label).join(', ');
          reasons.push(
            `Descuento por encima del tope autorizado en: ${labels}`
          );
        }
        // H% > 50 no deja stale_prices ni discount_violations: es la única causa
        // de derivación sin motivo listado. Nombrarla en vez de dejar ": ."
        // colgando (no fabricar un motivo de precio/descuento que no ocurrió).
        const detalle = reasons.length
          ? `: ${reasons.join(' y ')}`
          : ': el % H supera el 50%';
        setError(
          `El presupuesto NO se envió${detalle}. ` +
          'Quedó pendiente de aprobación de un administrador.'
        );
        await loadBudgetData();
        return { ok: false };
      }

      if (result.spawned_version_id) {
        setSuccess(`Se creó la versión ${result.spawned_version_number}`);
      } else {
        setSuccess(`Estado cambiado a ${getStatusLabel(result.estado)}`);
      }
      setStaleReport([]);
      await loadBudgetData();
      // pdf_job_id viene seteado cuando esta transición fue la que en efecto
      // envió (draft/approved -> sent): la propia transición ya encoló el PDF
      // (CLAUDE.md §5), así que el llamador no debe encolarlo de nuevo.
      return { ok: true, pdfJobId: result.pdf_job_id };
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 422 && detail?.code === 'delivery_date_required') {
        setDeliveryPrompt({
          open: true,
          target,
          value: detail.suggested || currentVersion.fecha_prevista_entrega || '',
          afterPdf,
          porcentajeFacturado: facturadoInput,
        });
        return { ok: false };
      }
      // 409: el presupuesto fue editado y ya no requiere aprobación; el backend
      // lo des-encoló a borrador (CLAUDE.md §5, PO 2026-09-15). Recargar para
      // reflejar el nuevo estado y mostrar el aviso.
      if (err.response?.status === 409 && detail?.code === 'no_longer_requires_approval') {
        setError(detail.message || 'Este presupuesto fue editado y ya no requiere aprobación');
        await loadBudgetData();
        return { ok: false };
      }
      setError(detail?.message || detail || 'Error al cambiar el estado');
      return { ok: false };
    }
  };

  /**
   * Corre la transición directamente al elegir un destino en el dropdown de
   * estado - sin diálogo de confirmación intermedio (era redundante: el
   * dropdown ya es la elección). Las puertas reales (fecha de entrega, precios
   * desactualizados, lock de FX) las aplica runTransition/el backend, no un
   * segundo prompt de "¿seguro?".
   */
  // Transiciones que SIEMPRE re-confirman la fecha prevista de entrega en un
  // prompt bloqueante antes de correr (CLAUDE.md §5, actualizado 2026-09-08):
  // solo Vender (sent -> sold). Enviar (draft/approved -> sent) ya no fuerza
  // este prompt proactivo - la fecha sigue siendo un campo opcional editable;
  // si el backend igual la requiere (nunca se cargó), el catch de
  // runTransition ya maneja ese 422 (delivery_date_required) mostrando el
  // mismo diálogo de forma reactiva, sin necesidad de listar 'sent' acá.
  const DELIVERY_RECONFIRM_TARGETS = ['sold'];

  const handleStatusSelect = async (target: string) => {
    if (!budget || !currentVersion || !target) return;
    // Rechazar exige motivo: abrir el diálogo y disparar la transición desde
    // handleConfirmReject, no acá (CLAUDE.md §5).
    if (target === 'rejected') {
      setSelectedStatus(target);
      setRejectDialog({ open: true, reason: '' });
      return;
    }
    setSelectedStatus(target);
    setSaving(true);
    try {
      // Guardar cambios antes de cambiar estado (incluye la fecha editada en
      // la sección Proyecto y Cliente).
      await updateAllBudgetData();
      // Enviar/Vender: abrir el prompt de fecha precargado con lo que ya tenga
      // la versión; la transición real la dispara handleConfirmDeliveryDate.
      if (DELIVERY_RECONFIRM_TARGETS.includes(target)) {
        setDeliveryPrompt({
          open: true,
          target,
          value:
            formData.currentVersion?.fecha_prevista_entrega ||
            currentVersion.fecha_prevista_entrega ||
            '',
          afterPdf: false,
          porcentajeFacturado: facturadoInput,
        });
        return;
      }
      const { ok } = await runTransition(target);
      if (ok) {
        setSelectedStatus('');
      }
    } finally {
      setSaving(false);
    }
  };

  // Confirmación del prompt bloqueante de fecha: reintenta la misma transición
  // que lo disparó, ahora con la fecha.
  const handleConfirmDeliveryDate = async () => {
    if (!deliveryPrompt.value) return;
    setSaving(true);
    try {
      const wasSold = deliveryPrompt.target === 'sold';
      const facturado = deliveryPrompt.porcentajeFacturado;
      const { ok, pdfJobId } = await runTransition(deliveryPrompt.target, deliveryPrompt.value);
      if (ok) {
        const wasPdf = deliveryPrompt.afterPdf;
        // Al vender, además guardar el % H confirmado en el popup. La versión ya
        // es 'sold' tras la transición, así que va por el camino allow_sold (no
        // corre la puerta de aprobación, no cambia el estado - CLAUDE.md §5/§8).
        if (wasSold && budget && currentVersion) {
          try {
            await BudgetApiService.updatePorcentajeH(
              budget.id, currentVersion.id, 100 - facturado, true
            );
            await loadBudgetData();
          } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(
              (typeof detail === 'string' ? detail : detail?.message)
              || 'La venta se registró, pero no se pudo guardar el % H'
            );
          }
        }
        setDeliveryPrompt({ open: false, target: '', value: '', afterPdf: false, porcentajeFacturado: 50 });
        setSelectedStatus('');
        if (wasPdf) await handleDownloadPdf(undefined, pdfJobId);
      }
    } finally {
      setSaving(false);
    }
  };

  // El % H ya no se guarda con un botón aparte (CLAUDE.md §5, PO 2026-09-15):
  // viaja en el guardado normal de la versión (buildFullSavePayload) y el gate
  // H>50% se evalúa al Enviar junto con precios/descuentos. El único uso directo
  // que queda de updatePorcentajeH es el popup de fecha al Vender (allow_sold).

  // Confirmación del diálogo de rechazo: corre pending_approval -> rejected con
  // el motivo. El backend rechaza un motivo vacío (reason_required).
  const handleConfirmReject = async () => {
    const reason = rejectDialog.reason.trim();
    if (!reason) return;
    setSaving(true);
    try {
      const { ok } = await runTransition('rejected', undefined, false, reason);
      if (ok) {
        setRejectDialog({ open: false, reason: '' });
        setSelectedStatus('');
      }
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // "ENVIAR A ERP" (CLAUDE.md §8.1-§8.12)
  // ==========================================
  const openErpDialog = () => {
    // Reset del diálogo cada vez que se abre; conserva secciones/último resultado
    // no aporta y confunde en un reintento.
    setErpSections({ materiales: true, mano_obra: false, flete: false });
    setErpClienteQuery('');
    setErpMatches([]);
    setErpSelectedCliente(null);
    setErpShowCreate(false);
    setErpResult(null);
    setErpError(null);
    setErpProcessing(false);
    setErpDialog(true);
  };

  const handleErpSearch = async () => {
    const q = erpClienteQuery.trim();
    if (!q) return;
    setErpSearching(true);
    setErpError(null);
    try {
      const matches = await apiService.erp.searchClientes(q);
      setErpMatches(matches);
      if (matches.length === 0) setErpShowCreate(true);
    } catch (err: any) {
      setErpError(err.response?.data?.detail || 'Error al buscar clientes en el ERP');
    } finally {
      setErpSearching(false);
    }
  };

  const handleErpCreateCliente = async () => {
    if (!erpCreateForm.razon_social.trim() || !erpCreateForm.cuit.trim()) return;
    setErpSubmitting(true);
    setErpError(null);
    try {
      const created = await apiService.erp.createCliente(erpCreateForm);
      setErpSelectedCliente(created);
      setErpShowCreate(false);
    } catch (err: any) {
      setErpError(err.response?.data?.detail || 'Error al crear el cliente en el ERP');
    } finally {
      setErpSubmitting(false);
    }
  };

  const handleErpSubmit = async () => {
    if (!currentVersion) return;
    const codigo = erpSelectedCliente?.Cliente_Codigo;
    if (!codigo) {
      setErpError('Seleccioná o creá un cliente antes de enviar.');
      return;
    }
    setErpSubmitting(true);
    setErpError(null);
    try {
      // El submit se encola como job de RQ (CLAUDE.md §3 / SUPERVISOR_NOTES
      // 2026-09-15): el gateway puede tardar 30-80s en frío y nunca debe
      // bloquear/cortar el request. Encolamos y sondeamos el estado.
      await apiService.erp.submitBudget(currentVersion.id, {
        cliente_codigo: String(codigo),
        include_materiales: erpSections.materiales,
        include_mano_obra: erpSections.mano_obra,
        include_flete: erpSections.flete,
      });

      // Poll rápido: un envío "en caliente" resuelve dentro de la ventana y se
      // siente sincrónico; si la excede (arranque en frío) mostramos
      // "procesando en segundo plano" y el rep puede irse.
      const POLL_INTERVAL_MS = 1500;
      const MAX_WAIT_MS = 15000;
      const startedAt = Date.now();
      let st = await apiService.erp.getSubmitStatus(currentVersion.id);
      while (st.erp_submission_status === 'queued' || st.erp_submission_status === 'in_progress') {
        if (Date.now() - startedAt > MAX_WAIT_MS) break;
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        st = await apiService.erp.getSubmitStatus(currentVersion.id);
      }

      if (st.erp_submission_status === 'done' || st.erp_submission_status === 'failed') {
        // El outcome ya está persistido en la versión (ids/error); armamos el
        // resultado inline. unresolved_skus no se persiste, así que en el path
        // async no se muestra (queda [] ); el path caliente igual lo cubre.
        setErpResult({
          estado: currentVersion.estado || 'sold',
          ok: st.ok,
          nph_required: !!st.erp_nph_document_id,
          erp_np_document_id: st.erp_np_document_id,
          erp_nph_document_id: st.erp_nph_document_id,
          submitted: { np: !!st.erp_np_document_id, nph: !!st.erp_nph_document_id },
          unresolved_skus: [],
          error: st.error,
        });
      } else {
        setErpProcessing(true);
      }
      // Reflejar los flags persistidos en la versión sin recargar todo: es lo
      // que lee la superficie pasiva "No cargado en ERP".
      setFormData(prev => ({
        ...prev,
        currentVersion: prev.currentVersion ? {
          ...prev.currentVersion,
          erp_np_document_id: st.erp_np_document_id,
          erp_nph_document_id: st.erp_nph_document_id,
          erp_submission_error: st.error,
        } : prev.currentVersion,
      }));
    } catch (err: any) {
      // Un 4xx es un guard real (no vendido / sin líneas / envío ya en curso):
      // mantiene su `detail`.
      setErpError(
        err.response?.data?.detail ||
        'No se pudo iniciar el envío a ERP. Reintentá; si el error persiste, avisá al administrador.'
      );
    } finally {
      setErpSubmitting(false);
    }
  };

  // Función para borrar presupuesto
  const handleDeleteBudget = async () => {
    if (!budget) return;
    
    setSaving(true);
    try {
      await BudgetApiService.deleteBudget(budget.id);
      setSuccess('Presupuesto eliminado exitosamente');
      navigate('/budgets');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al eliminar el presupuesto');
      setSaving(false);
    }
  };

  // Funciones de utilidad
  // Delegan en BudgetApiService en vez de repetir el switch: este componente
  // tenía su propia copia que ya había divergido (mostraba 'Cerrado' y un
  // 'rejected' que el backend nunca tuvo).
  const getStatusColor = (status: string) => BudgetApiService.getStatusColor(status);
  const getStatusLabel = (status: string) => BudgetApiService.getStatusLabel(status);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    // Siempre mostrar símbolo antes del valor, miles con punto y decimales con coma
    if (currency === 'USD') {
      return `U$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `AR$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  // ==========================================
  // FUNCIÓN DE DESCARGA DE PDF
  // ==========================================

  /**
   * Descargar PDF de una versión específica
   */
  const handleDownloadPdf = async (versionId?: number, preEnqueuedJobId?: string) => {
    if (!budget) return;

    try {
      setSaving(true);
      setError(null);

      // Usar la versión especificada o la actual
      const targetVersionId = versionId || currentVersion?.id;
      const isCurrentVersion = !versionId || targetVersionId === currentVersion?.id;

      console.log(`📄 Descargando PDF del presupuesto ${budget.id}, versión ${targetVersionId}...`);

      // El PDF es lo que va al cliente, así que descargarlo desde un borrador
      // ES el envío: acá corre la puerta de CLAUDE.md §5 (precios
      // desactualizados + fecha de entrega + lock de cotización) ANTES de
      // generar nada. Si la puerta no se pasa, no hay PDF: la versión quedó
      // pendiente de aprobación, o falta la fecha, y en ninguno de los dos
      // casos hay algo que mandarle al cliente.
      //
      // La propia transición ya encola el job de PDF al enviar (CLAUDE.md §5) -
      // `pdfJobId` es ESE job, no uno nuevo. Solo si viniera vacío (una
      // versión ya `sent`, o el encolado desde la transición falló) se cae al
      // /export/pdf de siempre más abajo.
      let jobId = preEnqueuedJobId;
      if (isCurrentVersion && currentVersion?.estado === 'draft') {
        if (isDirty) {
          await updateAllBudgetData();
        }

        // afterPdf: si falta la fecha, el prompt reintenta la transición y
        // recién ahí vuelve a esta descarga.
        const { ok: sent, pdfJobId } = await runTransition('sent', undefined, true);
        if (!sent) {
          setSaving(false);
          return;
        }
        jobId = pdfJobId;
      }

      // Generación en background (CLAUDE.md §3): encolar (salvo que la
      // transición ya lo haya hecho), sondear estado, y recién descargar
      // cuando el job termina - así esta llamada nunca mantiene una request
      // HTTP esperando un render lento.
      if (!jobId) {
        const started = await BudgetApiService.startPdfExport(budget.id, targetVersionId);
        jobId = started.job_id;
      }
      console.log(`⏳ Job de PDF encolado (${jobId}), esperando...`);

      const POLL_INTERVAL_MS = 1000;
      // El job de RQ tiene default_timeout=120s (backend jobs/pdf_jobs.py) y este
      // reloj arranca en el ENCOLADO, así que incluye la espera en cola. Un cap
      // por debajo de 120s + cola hacía que un render lento (o una cola en el pico
      // de fin de mes, CLAUDE.md §3) se leyera como "timeout" aunque el job
      // terminara bien. Le damos margen sobre el timeout del job + espera en cola.
      const MAX_WAIT_MS = 180000;
      const startedAt = Date.now();
      let jobStatus = 'queued';
      while (jobStatus !== 'finished') {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        const statusResult = await BudgetApiService.getPdfExportStatus(budget.id, jobId);
        jobStatus = statusResult.status;
        if (jobStatus === 'failed') {
          throw new Error(statusResult.error || 'La generación del PDF falló en el worker');
        }
        if (Date.now() - startedAt > MAX_WAIT_MS) {
          throw new Error(
            'Tiempo de espera agotado esperando el PDF. Si esto se repite, ' +
            'verificá que el worker de PDF (rq worker pdf) esté corriendo.'
          );
        }
      }

      const blob = await BudgetApiService.downloadPdfExport(budget.id, jobId);

      // Crear nombre de archivo: presupuesto_(nombre-proyecto)_(cliente)_v(version).pdf
      const versionNumber = versionId 
        ? versions.find(v => v.id === versionId)?.version_number 
        : currentVersion?.version_number;
      // Sanitizar nombre del proyecto y cliente para usar como nombre de archivo
      const proyectoSanitizado = budget.proyecto.replace(/\s+/g, '-').replace(/\//g, '-');
      const clienteSanitizado = budget.cliente?.replace(/\s+/g, '-').replace(/\//g, '-') || '';
      const fileName = clienteSanitizado 
        ? `presupuesto_${proyectoSanitizado}_${clienteSanitizado}_v${versionNumber || 'latest'}.pdf`
        : `presupuesto_${proyectoSanitizado}_v${versionNumber || 'latest'}.pdf`;
      
      // Crear link de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF descargado correctamente');
      setSuccess('PDF descargado correctamente. La versión se ha cerrado.');
      
      // Si cambiamos el estado, recargar datos para actualizar la UI
      if (isCurrentVersion && currentVersion?.estado === 'draft') {
        console.log('🔄 Recargando datos del presupuesto...');
        await loadBudgetData();
        setIsDirty(false); // Limpiar dirty flag ya que recargamos todo
      }
      
    } catch (error: any) {
      console.error('❌ Error descargando PDF:', error);
      // Surface the real cause (worker error, timeout, gate) instead of a
      // generic string - antes esto ocultaba todo detrás de "Intenta nuevamente"
      // y un worker caído se veía igual que un bug de render.
      const detail = error?.response?.data?.detail;
      const msg =
        detail?.message || detail || error?.message ||
        'Error al descargar el PDF. Intenta nuevamente.';
      setError(`Error al descargar el PDF: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // GUARDADO (bulk, transaccional - CLAUDE.md §10 field reports #2 y #7)
  // ==========================================
  //
  // Esto reemplaza lo que antes era: updateBudgetBasicData() + un loop
  // await-por-ítem (un ambiente, luego sus materiales, luego sus
  // instalaciones, luego cada flete - todo secuencial, una request HTTP por
  // ítem). Para un presupuesto con muchas áreas eso eran decenas o cientos
  // de round-trips sin ninguna transacción: una sola request fallando a
  // mitad de camino (nada exótico - alcanza un corte de red momentáneo)
  // dejaba el presupuesto parcialmente guardado del lado del servidor, sin
  // forma de recuperarlo salvo recargando la página. Esa era la causa real
  // de field report #2. Ahora es una sola llamada a
  // PUT .../versions/{id}/full, que reconcilia TODO el estado de la versión
  // en una transacción atómica del backend: o se guarda todo, o (ante
  // cualquier error) no se guarda nada.

  // Token de idempotencia: se genera una vez por tanda de cambios sin
  // guardar y se reutiliza en cada reintento hasta que un guardado tenga
  // éxito. Así, si la respuesta de un intento se pierde pero el guardado ya
  // se había aplicado del lado del servidor, el backend lo detecta (mismo
  // token) y devuelve el estado actual en vez de volver a crear todo de
  // nuevo con ids duplicados.
  const saveTokenRef = useRef<string | null>(null);
  const getOrCreateSaveToken = (): string => {
    if (!saveTokenRef.current) {
      saveTokenRef.current = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    return saveTokenRef.current;
  };

  /**
   * Arma el payload completo de la versión actual a partir de formData, en
   * el formato que espera PUT /budgets/{id}/versions/{id}/full.
   */
  // Los overrides permiten construir el payload a partir de una lista efectiva
  // (comprometidos + fila inline staged) sin depender de que setFormData ya
  // haya aplicado (setState es async) - ver updateAllBudgetData
  // (fix/mo-flete-staged-row-capture). Sin overrides, se comporta igual que antes.
  const buildFullSavePayload = (
    environmentsOverride?: typeof formData.environments,
    freightsOverride?: typeof formData.freights,
  ): BudgetVersionFullSave => ({
    titulo: formData.currentVersion?.titulo || '',
    vendedor: formData.currentVersion?.vendedor || '',
    email_vendedor: formData.currentVersion?.email_vendedor || '',
    tel_vendedor: formData.currentVersion?.tel_vendedor || '',
    condiciones_contratacion: formData.currentVersion?.condiciones_contratacion || '',
    condiciones_adicionales: formData.currentVersion?.condiciones_adicionales || '',
    descuento_instalacion: formData.descuento_instalacion || 0,
    descuento_fletes: formData.descuento_fletes || 0,
    price_list_id: formData.currentVersion?.price_list_id,
    // Fecha prevista de entrega (CLAUDE.md §5) - ahora un campo editable en la
    // sección Proyecto y Cliente, así que viaja en el guardado normal. '' -> se
    // envía undefined para no pisar una fecha ya cargada con vacío.
    fecha_prevista_entrega: formData.currentVersion?.fecha_prevista_entrega || undefined,
    environments: (environmentsOverride ?? formData.environments).map(env => ({
      id: env.id,
      nombre: env.nombre,
      orden: env.orden,
      // % "Aplicar descuento" del ambiente (CLAUDE.md §8.7 outlet model).
      descuento_materiales: env.descuento_materiales || 0,
      materials: env.materials.map(mat => ({
        id: mat.id,
        product_id: mat.product_id,
        descripcion: mat.descripcion,
        codigo: mat.codigo,
        unidades: mat.unidades,
        metros2: mat.metros_cuadrados || undefined, // Solo incluir si tiene valor (estructura no tiene m2)
        precio_unitario: mat.precio_unitario,
        monto: mat.unidades * mat.precio_unitario,
        currency: mat.currency,
        observaciones: mat.observaciones,
        es_in_situ: mat.es_in_situ,
        con_desperdicio: mat.con_desperdicio || false,
        con_estructura: mat.con_estructura || false,
        // Descuento aplicado de la línea (CLAUDE.md §8.7). NO se envía
        // `descuento_producto` (lo gestiona el servidor).
        descuento_linea: mat.descuento_linea || 0,
      })),
      installations: (env.installations || []).map(inst => {
        const esCustom = inst.tipo_instalacion === 'custom' || !inst.installation_service_id;
        const esGlobal = esCustom && inst.unidad?.toLowerCase() === 'global';
        // Cada variante ya es su propia fila del catálogo con su propio
        // precio (CLAUDE.md §7, decidido 2026-08-30) - sin rama por tipo.
        let mano_obra_m2 = inst.mano_obra_m2;
        if ((!inst.mano_obra_m2 || inst.mano_obra_m2 === 0) && inst.installation_service) {
          mano_obra_m2 = inst.installation_service.precio || 0;
        }
        const cantidad = inst.cantidad_manual ?? 1;
        const subtotal = esGlobal ? (mano_obra_m2 ?? 0) : (mano_obra_m2 ?? 0) * cantidad;
        return {
          id: inst.id,
          installation_service_id: inst.installation_service_id,
          tipo_instalacion: inst.tipo_instalacion as any,
          cantidad_manual: cantidad,
          trabajo: inst.trabajo,
          descripcion_trabajo: inst.descripcion_trabajo,
          mano_obra_m2,
          unidad: esCustom ? (inst.unidad || '') : undefined,
          subtotal,
        };
      }),
    })),
    freights: (freightsOverride ?? formData.freights).map(freight => ({
      id: freight.id,
      tipo_envio: freight.tipo_envio,
      descripcion_detalle: freight.descripcion_detalle,
      monto: freight.monto,
      currency: freight.currency,
      bonificado: freight.bonificado || false,
      // FK to the FreightRate catalog row (CLAUDE.md §8.5) - undefined for a
      // manual entry. Without this the full-save would silently strip the
      // link off any freight that has one.
      freight_rate_id: freight.freight_rate_id,
    })),
    nombre_version: formData.currentVersion?.nombre_version,
    // % H = 100 - % Facturado (CLAUDE.md §5, PO 2026-09-15). Se guarda con el
    // resto de la versión; el gate H>50% lo evalúa el backend al Enviar.
    porcentaje_h: 100 - facturadoInput,
    save_token: getOrCreateSaveToken(),
  });

  /**
   * Vuelca al estado local los ids reales que el backend asignó a
   * ambientes/materiales/instalaciones/fletes nuevos, emparejando por
   * posición contra lo que se envió (el backend procesa la lista en el
   * mismo orden). Deliberadamente NO reemplaza formData entero por la
   * respuesta del servidor - eso perdería campos que solo existen en el
   * estado local (como el objeto `product` completo de un material recién
   * agregado) y, más importante, dispara `loading` en loadBudgetData(),
   * lo que volaría todo el formulario en cada autoguardado.
   */
  const applySavedIds = (saved: any) => {
    setFormData(prev => ({
      ...prev,
      environments: prev.environments.map((env, envIdx) => {
        const savedEnv = saved?.environments?.[envIdx];
        if (!savedEnv) return env;
        return {
          ...env,
          id: savedEnv.id,
          materials: env.materials.map((mat, matIdx) => {
            const savedMat = savedEnv.materials?.[matIdx];
            return savedMat ? { ...mat, id: savedMat.id } : mat;
          }),
          installations: (env.installations || []).map((inst, instIdx) => {
            const savedInst = savedEnv.installations?.[instIdx];
            return savedInst ? { ...inst, id: savedInst.id } : inst;
          }),
        };
      }),
      freights: prev.freights.map((freight, freightIdx) => {
        const savedFreight = saved?.freights?.[freightIdx];
        return savedFreight ? { ...freight, id: savedFreight.id } : freight;
      }),
    }));
  };

  /**
   * Guardar TODO el presupuesto (datos básicos + ambientes + materiales +
   * instalaciones + fletes) en una sola llamada transaccional.
   */
  const updateAllBudgetData = async () => {
    if (!budget || !currentVersion) return;

    try {
      setSaving(true);
      console.log('🔄 Guardando presupuesto...');

      const budgetUpdateData = {
        proyecto: formData.proyecto,
        cliente: formData.cliente,
        responsable: formData.responsable,
        email_cliente: formData.email_cliente,
        tel_cliente: formData.tel_cliente,
        status: formData.status
      };
      await BudgetApiService.updateBudget(budget.id, budgetUpdateData);

      // Red de seguridad (fix/mo-flete-staged-row-capture): una fila inline de
      // Mano de Obra / Flete que el usuario llenó pero NO confirmó con su botón
      // "Agregar" vive todavía en `newInstallation`/`newFreight` (estado staged),
      // fuera de `formData`, y se perdía en silencio al guardar. La detectamos y
      // la incluimos aplicando las MISMAS validaciones/normalización que
      // handleAddInstallation/handleAddFreight. setState es async, así que NO
      // llamamos al add-handler y leemos formData en el mismo tick: computamos el
      // efectivo (comprometidos + staged) y construimos el payload de ahí.
      const stagedInst = stagedInstallationHasData(newInstallation, installationMode)
        ? normalizeStagedInstallation(newInstallation, installationMode)
        : null;
      const effectiveEnvironments = computeEffectiveInstallationEnvironments(
        formData.environments,
        stagedInst,
        selectedEnvironmentIndex,
        {
          generalEnvName: GENERAL_INSTALLATIONS_ENV_NAME,
          makeGeneralEnv: (inst) => ({
            nombre: GENERAL_INSTALLATIONS_ENV_NAME,
            descripcion: 'Instalaciones generales del presupuesto',
            orden: 9999,
            materials: [],
            installations: [inst],
          }),
        },
      );
      const stagedFreight = stagedFreightHasData(newFreight, freightMode) ? { ...newFreight } : null;
      const effectiveFreights = computeEffectiveFreights(formData.freights, stagedFreight);

      if (stagedInst || stagedFreight) {
        // Comprometer la fila staged en formData ANTES del guardado, para que
        // (a) persista y no se pierda y (b) applySavedIds -que empareja por
        // posición- vea el mismo orden que se envió en el payload. Limpiar el
        // estado staged para que un re-guardado no la agregue dos veces (req. #2).
        setFormData(prev => ({ ...prev, environments: effectiveEnvironments, freights: effectiveFreights }));
        if (stagedInst) {
          setNewInstallation({ trabajo: '', mano_obra_m2: 0, cantidad_manual: 1 });
          setInstallationMode('catalog');
        }
        if (stagedFreight) {
          setNewFreight({ tipo_envio: '', monto: 0, tipo: 'fijo', currency: 'ARS', bonificado: false });
          setFreightMode('catalog');
        }
      }

      const payload = buildFullSavePayload(effectiveEnvironments, effectiveFreights);
      const saved = await BudgetApiService.saveFullVersion(budget.id, currentVersion.id, payload);
      applySavedIds(saved);
      // Defect 1: un full-save puede des-encolar la versión (pending_approval ->
      // draft) o cambiar su estado, pero applySavedIds solo copia ids. Adoptar el
      // estado y los reportes de la respuesta para que el chip de Estado y los
      // banners (pending_approval / rejected) se sincronicen sin recargar.
      const syncVersionStatus = <T extends BudgetVersion>(v: T): T => ({
        ...v,
        estado: saved.estado,
        stale_price_report: saved.stale_price_report,
        discount_cap_violation_report: saved.discount_cap_violation_report,
        rejection_reason: saved.rejection_reason,
      });
      setFormData(prev => ({
        ...prev,
        currentVersion: prev.currentVersion ? syncVersionStatus(prev.currentVersion) : prev.currentVersion,
      }));
      setCurrentVersion(prev => (prev ? syncVersionStatus(prev) : prev));
      // Los totales pueden haber cambiado sin que cambie el id de la
      // versión - refrescar el resumen canónico en vez de dejar los montos
      // mostrados desincronizados hasta el próximo cambio de versión.
      refreshVersionSummary(currentVersion.id);

      // Guardado con éxito: el próximo cambio (después de este) genera un
      // token nuevo, distinto de esta tanda ya aplicada.
      saveTokenRef.current = null;

      console.log('✅ Presupuesto guardado');
      setSuccess('Todos los cambios han sido guardados correctamente');
      handleAfterSave();
    } catch (error) {
      console.error('❌ Error guardando el presupuesto:', error);
      setError('Error al guardar los cambios. Intenta nuevamente.');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // El guardado es SIEMPRE explícito (botón "Actualizar Presupuesto"). No hay
  // autoguardado periódico ni autoguardado silencioso al navegar: el usuario
  // pidió control total sobre cuándo se persiste, y la protección contra perder
  // cambios sin guardar la da `useUnsavedChangesGuard(isDirty)` (arriba), que
  // avisa antes de salir en vez de guardar por su cuenta. (Nota: esto deja en
  // pausa la "red de seguridad" de autoguardado que sugería el field report #4;
  // si se quiere recuperación ante crash, habría que reintroducirla como algo
  // que NO persista silenciosamente sin intención del usuario.)

  // Los totales por sección (Materiales c/IVA, Mano de Obra convertida,
  // Flete neto de Bonificado) ya NO se recalculan acá - getTotalMaterials/
  // getTotalMaterialsWithDiscounts/getTotalInstallationsARS/
  // getTotalInstallations/getTotalFreights vivían en este lugar y quedaron
  // retirados a favor de `versionSummary` (GET .../summary, CLAUDE.md
  // §6/§10 field report #6) - ver más arriba, cerca de `currentVersion`.

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!budget) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Presupuesto no encontrado
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ width: '80%', margin: '0 auto' }}>
      {/* Header con botón de volver y acciones */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => {
            // Sin autoguardado: si hay cambios sin guardar,
            // useUnsavedChangesGuard intercepta este navigate y pide confirmación.
            navigate('/budgets');
          }}
        >
          Volver a Presupuestos
        </Button>
        
        {/* Botones de acción */}
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Guardado explícito: la única forma de persistir cambios. Se habilita
              solo cuando hay cambios sin guardar (isDirty) y la versión es
              editable (no sent/sold/lost). No autoguarda nada por su cuenta. */}
          {!isFieldsReadOnly && (
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
              disabled={!isDirty || saving}
              onClick={() => {
                updateAllBudgetData().catch(() => {
                  // El error ya se muestra vía setError() dentro de updateAllBudgetData.
                });
              }}
            >
              Actualizar Presupuesto
            </Button>
          )}
          {/* Item E: Estado como dropdown (reemplaza el Chip de solo-lectura y
              el botón "Cambiar Estado"). Ofrece SÓLO las transiciones válidas
              del state-machine (statusDropdownTargets) y, al elegir una, corre
              la transición GATEADA real (fecha, precios, lock de FX, derivación
              a aprobación) directamente via handleStatusSelect - sin diálogo de
              confirmación intermedio, nunca un set de estado a ciegas. */}
          {canChangeStatus && statusDropdownTargets.length > 0 ? (
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={saving}>
              <InputLabel id="estado-select-label">Estado</InputLabel>
              <Select
                labelId="estado-select-label"
                label="Estado"
                value={versionEstado}
                renderValue={() => getStatusLabel(versionEstado)}
                onChange={(e) => {
                  const target = e.target.value;
                  if (target && target !== versionEstado) {
                    handleStatusSelect(target);
                  }
                }}
              >
                <MenuItem value={versionEstado} disabled>
                  {getStatusLabel(versionEstado)} (actual)
                </MenuItem>
                {statusDropdownTargets.map(target => (
                  <MenuItem value={target} key={target}>
                    Cambiar a: {getStatusLabel(target)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            // Sin transiciones disponibles: mostrar el estado actual, no editable.
            // "No Aprobado": el motivo del rechazo aparece al pasar el mouse.
            <Tooltip
              title={
                versionEstado === 'rejected'
                  ? (formData.currentVersion?.rejection_reason || '')
                  : ''
              }
            >
              <Chip
                label={getStatusLabel(versionEstado)}
                color={getStatusColor(versionEstado)}
                variant="filled"
              />
            </Tooltip>
          )}

          {/* Item E: "Descargar PDF" sólo visible una vez enviada la versión
              (sent/sold/lost) - antes de eso no hay PDF generado. */}
          {isSentLike && (
            <Button
              startIcon={<Download />}
              variant="outlined"
              onClick={async () => {
                try {
                  // Guardar cambios primero
                  await updateAllBudgetData();
                  // Luego descargar la última versión (versión actual)
                  await handleDownloadPdf();
                } catch (error) {
                  setError('Error al guardar cambios o descargar PDF.');
                }
              }}
            >
              Descargar PDF
            </Button>
          )}

          {/* Phase 8.7 Round A, item 4: ya no es admin-only - un `user`
              regular puede eliminar sus propios presupuestos (el backend
              en budgets.py ya usaba `user_has_access`, admin O dueño, así
              que esto solo ensancha el gate del frontend para que coincida). */}
          {!isReadOnly && (user?.role === 'admin' || budget?.user_id === user?.id) && (
            <Button
              startIcon={<Delete />}
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialog(true)}
            >
              Eliminar
            </Button>
          )}

          {/* Item E: "Crear Nueva Versión" sólo a partir de una versión enviada
              (sent/sold/lost) - spawnear desde un borrador nunca enviado no
              tiene sentido (CLAUDE.md §5). Placement único: sólo acá (se quitó
              el botón duplicado del header de "Historial de Versiones"). */}
          {isSentLike && (
            <Button
              startIcon={<Add />}
              variant="contained"
              color="primary"
              onClick={() => setNewVersionDialog(true)}
            >
              Crear Nueva Versión
            </Button>
          )}

          {/* "Enviar a ERP" (CLAUDE.md §8.1): sólo cuando la versión está vendida
              (sold), separado del propio sent->sold. Abre el diálogo de secciones
              + cliente + envío. */}
          {versionEstado === 'sold' && (
            <Button
              startIcon={<CloudUpload />}
              variant="contained"
              color="secondary"
              onClick={openErpDialog}
            >
              Enviar a ERP
            </Button>
          )}
        </Stack>
      </Box>

      {/* Título centrado */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          {formData.proyecto}
        </Typography>
        {/* Subtítulo: Versión · Código · Último guardado, todo en el mismo
            tamaño/fuente (variant h6, text.secondary). Se muestra en cualquier
            estado del presupuesto abierto. */}
        <Box display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" gap={3} mt={2}>
          {currentVersion && (
            editingVersionName ? (
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  value={editingVersionNameValue}
                  onChange={(e) => setEditingVersionNameValue(e.target.value)}
                  placeholder={`Versión ${currentVersion.version_number}`}
                  autoFocus
                  inputProps={{ maxLength: 100 }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    const trimmed = editingVersionNameValue.trim();
                    setFormData(prev => ({
                      ...prev,
                      currentVersion: prev.currentVersion
                        ? { ...prev.currentVersion, nombre_version: trimmed || undefined }
                        : prev.currentVersion
                    }));
                    setIsDirty(true);
                    setEditingVersionName(false);
                  }}
                >
                  <CheckCircle />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => setEditingVersionName(false)}>
                  <Cancel />
                </IconButton>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" color="text.secondary">
                  {currentVersion.nombre_version || `Versión ${currentVersion.version_number}`}
                </Typography>
                {!(isReadOnly || isFieldsReadOnly) && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingVersionNameValue(currentVersion.nombre_version || '');
                      setEditingVersionName(true);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )
          )}
          <Typography variant="h6" color="text.secondary">
            Código: {formData.code}
          </Typography>
          {currentVersion && (
            // Último guardado: fecha + hora (CLAUDE.md §10 report #9). Cae a
            // created_at si la versión nunca tuvo un full-save.
            <Typography variant="h6" color="text.secondary">
              Último guardado: {(currentVersion.last_saved_at || currentVersion.created_at)
                ? new Date(currentVersion.last_saved_at || currentVersion.created_at).toLocaleString()
                : '-'}
            </Typography>
          )}
        </Box>
        {/* Item E: el estado ahora vive en el dropdown de la barra de acciones
            (arriba), no como un Chip de solo-lectura acá. */}
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Por qué la versión está en "Pendiente de aprobación" (CLAUDE.md §5/§8.7).
          Se lee del reporte persistido de la versión, así que se ve igual recién
          derivado que reabriendo esta pantalla más tarde. Antes vivía dentro del
          StatusDialog (ya retirado); ahora es un alert inline permanente mientras
          la versión siga pendiente. */}
      {versionEstado === 'pending_approval' && (
        <>
          {(formData.currentVersion?.stale_price_report?.mismatches?.length ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Precios de Materiales desactualizados
              </Typography>
              {formData.currentVersion!.stale_price_report!.mismatches.map((m, i) => (
                <Typography key={i} variant="body2">
                  {m.codigo}{m.descripcion ? ` - ${m.descripcion}` : ''}: cargado a US$ {m.captured_price?.toFixed(2)}
                  {m.current_price != null
                    ? `, ahora US$ ${m.current_price.toFixed(2)} en la lista`
                    : ', ya no tiene precio vigente en la lista'}
                </Typography>
              ))}
            </Alert>
          )}
          {(formData.currentVersion?.discount_cap_violation_report?.violations?.length ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Descuento por encima del tope del vendedor
              </Typography>
              {formData.currentVersion!.discount_cap_violation_report!.violations.map((v, i) => (
                <Typography key={i} variant="body2">
                  {v.section_label}: {v.descuento_pct}% de descuento, tope autorizado {v.cap_pct}%
                </Typography>
              ))}
            </Alert>
          )}
        </>
      )}

      {/* "No Aprobado" (CLAUDE.md §5): motivo del rechazo, mismo slot que el
          banner de pending_approval. La versión es editable: el vendedor
          corrige y reenvía. */}
      {versionEstado === 'rejected' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Presupuesto no aprobado
          </Typography>
          <Typography variant="body2">
            {formData.currentVersion?.rejection_reason
              || 'Un administrador rechazó esta versión.'}
          </Typography>
        </Alert>
      )}

      {/* Superficie pasiva "No cargado en ERP" (CLAUDE.md §8.12). Se muestra
          cuando quedó un error de envío o falta algún comprobante (NP/NPH) en
          una versión vendida. El reintento es admin-only; re-abre el diálogo de
          envío (el backend re-intenta sólo el comprobante faltante, sin duplicar). */}
      {versionEstado === 'sold' && formData.currentVersion?.erp_submission_error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={isAdmin ? (
            <Button color="inherit" size="small" onClick={openErpDialog}>
              Reintentar
            </Button>
          ) : undefined}
        >
          <Typography variant="subtitle2" gutterBottom>
            No cargado en ERP
          </Typography>
          <Typography variant="body2">
            {formData.currentVersion?.erp_submission_error
              || 'El envío al ERP no se completó.'}
          </Typography>
          {(formData.currentVersion?.erp_np_document_id || formData.currentVersion?.erp_nph_document_id) && (
            <Typography variant="body2">
              {formData.currentVersion?.erp_np_document_id
                ? `NP: ${formData.currentVersion.erp_np_document_id}`
                : 'NP: pendiente'}
              {' · '}
              {formData.currentVersion?.erp_nph_document_id
                ? `NPH: ${formData.currentVersion.erp_nph_document_id}`
                : 'NPH: pendiente'}
            </Typography>
          )}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Card de Información General - Proyecto/Cliente vs Vendedor */}
        <Card>
          <CardHeader 
            title="Información del Proyecto"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<Business color="primary" />}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }} sx={{ mx: 3 }}>
              {/* Información del Proyecto y Cliente */}
              <Box flex="1">
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Proyecto y Cliente
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Nombre del Proyecto"
                    value={formData.proyecto}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, proyecto: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly ||isFieldsReadOnly }}
                  />
                  <TextField
                    label="Cliente"
                    value={formData.cliente}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, cliente: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                  <TextField
                    label="Responsable"
                    value={formData.responsable}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, responsable: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                  <TextField
                    label="Email Cliente"
                    value={formData.email_cliente}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, email_cliente: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                  <TextField
                    label="Teléfono Cliente"
                    value={formData.tel_cliente}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      setFormData(prev => ({ ...prev, tel_cliente: e.target.value }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                  {/* Fecha prevista de entrega (CLAUDE.md §5). Campo editable
                      normal acá; además se re-confirma en un prompt bloqueante
                      al Enviar (draft->sent) y al Vender (sent->sold). */}
                  <TextField
                    label="Fecha prevista de entrega"
                    type="date"
                    value={formData.currentVersion?.fecha_prevista_entrega || ''}
                    onChange={e => {
                      if (isReadOnly || isFieldsReadOnly) return;
                      const value = e.target.value || undefined;
                      setFormData(prev => ({
                        ...prev,
                        currentVersion: prev.currentVersion
                          ? { ...prev.currentVersion, fecha_prevista_entrega: value }
                          : null
                      }));
                      setIsDirty(true);
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                  />
                  {/* % H / Calculadora (CLAUDE.md §5, PO 2026-09-15). El % H se
                      setea acá (en términos de % Facturado = 100 - H, como en la
                      Calculadora); Guardar corre la puerta de aprobación si H >
                      50%. El botón Calculadora H abre el split para screenshot. */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      label="% Facturado"
                      type="number"
                      value={facturadoInput}
                      onChange={e => {
                        if (isReadOnly || isFieldsReadOnly) return;
                        const v = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                        setFacturadoInput(v);
                        // El % H viaja en el guardado normal (CLAUDE.md §5, PO
                        // 2026-09-15): marcar dirty para que "Actualizar
                        // Presupuesto" se habilite y lo persista.
                        setIsDirty(true);
                      }}
                      disabled={isReadOnly || isFieldsReadOnly}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      helperText={`% H (sin factura): ${(100 - facturadoInput).toFixed(2)}% (máx 50%). Se guarda con el presupuesto.`}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="text"
                      startIcon={<Calculate />}
                      onClick={() => setHModalOpen(true)}
                      sx={{ mt: 1 }}
                    >
                      Calculadora H
                    </Button>
                  </Box>
                </Stack>
              </Box>

              {/* Información del Vendedor - Alineada a la derecha */}
              <Box flex="1" textAlign="right">
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Datos del Vendedor
                </Typography>
                {currentVersion ? (
                  <Stack spacing={2}>
                    <TextField
                      label="Vendedor"
                      value={formData.currentVersion?.vendedor || ''}
                      onChange={e => {
                        if (isReadOnly || isFieldsReadOnly) return;
                        setFormData(prev => ({
                          ...prev,
                          currentVersion: prev.currentVersion ? { ...prev.currentVersion, vendedor: e.target.value } : null
                        }));
                        setIsDirty(true);
                      }}
                      fullWidth
                      InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                    />
                    <TextField
                      label="Email Vendedor"
                      value={formData.currentVersion?.email_vendedor || ''}
                      onChange={e => {
                        if (isReadOnly || isFieldsReadOnly) return;
                        setFormData(prev => ({
                          ...prev,
                          currentVersion: prev.currentVersion ? { ...prev.currentVersion, email_vendedor: e.target.value } : null
                        }));
                        setIsDirty(true);
                      }}
                      fullWidth
                      InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                    />
                    <TextField
                      label="Teléfono Vendedor"
                      value={formData.currentVersion?.tel_vendedor || ''}
                      onChange={e => {
                        if (isReadOnly || isFieldsReadOnly) return;
                        setFormData(prev => ({
                          ...prev,
                          currentVersion: prev.currentVersion ? { ...prev.currentVersion, tel_vendedor: e.target.value } : null
                        }));
                        setIsDirty(true);
                      }}
                      fullWidth
                      InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
                    />
                    {/* Lista de precios (CLAUDE.md §4) - gobierna qué
                        productos de Materiales se pueden agregar/con qué
                        precio; ver isProductGated en el picker de abajo. */}
                    <FormControl fullWidth disabled={isReadOnly || isFieldsReadOnly}>
                      <InputLabel>Lista de Precios</InputLabel>
                      <Select
                        value={formData.currentVersion?.price_list_id ?? ''}
                        label="Lista de Precios"
                        onChange={e => {
                          if (isReadOnly || isFieldsReadOnly) return;
                          // IDs empiezan en 1: un valor "falsy" (incluido el '' del
                          // MenuItem "Sin lista asignada") siempre significa "ninguna".
                          const value = e.target.value ? Number(e.target.value) : undefined;
                          setFormData(prev => ({
                            ...prev,
                            currentVersion: prev.currentVersion ? { ...prev.currentVersion, price_list_id: value } : null
                          }));
                          setIsDirty(true);
                        }}
                      >
                        <MenuItem value="">
                          <em>Sin lista asignada</em>
                        </MenuItem>
                        {myPriceLists.map(pl => (
                          <MenuItem key={pl.id} value={pl.id}>{pl.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!(isReadOnly || isFieldsReadOnly) && !formData.currentVersion?.price_list_id && (
                      <Alert severity="warning">
                        Sin una lista de precios elegida no se pueden agregar materiales en
                        Ambientes - elegí una lista para habilitar la carga.
                      </Alert>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ mx: 3 }}>
                    <Typography variant="body1">No hay versión actual disponible</Typography>
                  </Alert>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Card de Ambientes y Materiales - Reutilizando estilo del form */}
        <Card>
          <CardHeader 
            title="Ambientes y Materiales"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<Home color="primary" />}
            subheader={`${formData.environments.length} ambiente(s) configurado(s)`}
            subheaderTypographyProps={{ variant: 'body1' }}
            sx={{ pb: 1 }}
            action={
              !(isReadOnly || isFieldsReadOnly) && (
                <Box sx={{ mr: 5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setEnvironmentDialog(true)}
                  >
                    Agregar Ambiente
                  </Button>
                </Box>
              )
            }
          />
          <CardContent sx={{ p: 3 }}>
            {formData.environments.length > 0 ? (
              <Box>
                {formData.environments
                  .filter(env => env.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                  .map((env, envIndex, filteredEnvs) => {
                    // Obtener el índice real en formData.environments
                    const actualEnvIndex = formData.environments.findIndex(e => e === env);
                    const isAddingHere = materialDialog && selectedEnvironmentIndex === actualEnvIndex;
                    // Subtotal del ambiente neto del descuento de cada línea
                    // (`descuento_linea`, CLAUDE.md §8.7 outlet model) - misma
                    // cuenta que BudgetService._compute_section_amounts.
                    const lineNet = (mat: MaterialFormData) =>
                      mat.unidades * mat.precio_unitario * (1 - (mat.descuento_linea || 0));
                    const subtotalUsd = env.materials
                      .filter(m => m.currency === 'USD')
                      .reduce((total, mat) => total + lineNet(mat), 0);
                    const subtotalArs = env.materials
                      .filter(m => m.currency === 'ARS')
                      .reduce((total, mat) => total + lineNet(mat), 0);
                    // Suma en $ de los descuentos por línea de este ambiente
                    // (línea "Descuento"). Cliente-side, reacciona en vivo.
                    const descuentoProductosUsd = env.materials
                      .filter(m => m.currency === 'USD')
                      .reduce((total, mat) => total + (mat.unidades * mat.precio_unitario *
                        (mat.descuento_linea || 0)), 0);
                    const descuentoTildado = env.descuentoTildado || (env.descuento_materiales || 0) > 0;
                    const setAmbientePct = (pct: number, tildado: boolean) => {
                      setFormData(prev => ({
                        ...prev,
                        environments: prev.environments.map((e, i) => i === actualEnvIndex
                          ? { ...applyAmbienteDiscount(e, pct), descuentoTildado: tildado } : e),
                      }));
                      setIsDirty(true);
                    };
                    const materialesCap = getMaterialesDiscountCap(formData.currentVersion?.price_list_id);
                    return (
                      <Box key={actualEnvIndex} sx={{ mb: 4 }}>
                        {/* Encabezado del ambiente - antes era una fila de tabla con
                            colspan; ahora es un subtítulo propio arriba de la tabla
                            de ESTE ambiente (cada ambiente tiene su propia tabla). */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {editingEnvironmentId === env.id ? (
                              // Modo edición: TextField con botones
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                  size="small"
                                  value={editingEnvironmentName}
                                  onChange={(e) => setEditingEnvironmentName(e.target.value)}
                                  autoFocus
                                  disabled={savingEnvironmentName}
                                  sx={{ minWidth: 200 }}
                                />
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSaveEnvironmentName(env.id!)}
                                  disabled={savingEnvironmentName || !editingEnvironmentName.trim()}
                                >
                                  <CheckCircle />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleCancelEditEnvironmentName}
                                  disabled={savingEnvironmentName}
                                >
                                  <Cancel />
                                </IconButton>
                              </Box>
                            ) : (
                              // Modo visualización: Nombre con ícono de editar
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" fontWeight="bold">
                                  📍 {env.nombre}
                                </Typography>
                                {!(isReadOnly || isFieldsReadOnly) && env.id && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleStartEditEnvironmentName(env.id!, env.nombre)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                  {env.descripcion || 'Sin descripción'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            {/* "Aplicar descuento" del ambiente (CLAUDE.md §8.7 outlet
                                model): llena el % de cada línea no-outlet. */}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={descuentoTildado}
                                  disabled={isReadOnly || isFieldsReadOnly}
                                  onChange={(e) => setAmbientePct(0, e.target.checked)}
                                />
                              }
                              label="Aplicar descuento"
                            />
                            {descuentoTildado && (
                              <TextField
                                type="number"
                                size="small"
                                value={env.descuento_materiales || ''}
                                disabled={isReadOnly || isFieldsReadOnly}
                                onChange={(e) => setAmbientePct(Math.min(Math.max(Number(e.target.value) || 0, 0), 99.99), true)}
                                inputProps={{ min: 0, max: 99.99, step: 1, style: { width: '60px', textAlign: 'right' } }}
                                InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
                              />
                            )}
                            {materialesCap !== null && env.materials.some(m => !isOutletLine(m) && (m.descuento_linea || 0) * 100 > materialesCap) && (
                              <Typography variant="caption" color="warning.main" sx={{ maxWidth: 260 }}>
                                Supera tu tope de descuento ({materialesCap}%): requerirá aprobación al enviar.
                              </Typography>
                            )}
                            {!(isReadOnly || isFieldsReadOnly) && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteEnvironment(actualEnvIndex)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Box>
                        </Box>

                        {!(isReadOnly || isFieldsReadOnly) && !formData.currentVersion?.price_list_id && (
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            Elegí una lista de precios (arriba) para poder agregar
                            materiales en este ambiente.
                          </Alert>
                        )}

                        <Box display="flex" justifyContent="center">
                          <TableContainer component={Paper} sx={{ maxWidth: 'calc(100% - 40px)', width: '100%' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio Unitario</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Unidades</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>M2</TableCell>
                                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Descuento</TableCell>
                                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {/* "+" inline en vez del botón "Agregar Material" del
                                    header (Phase 8.7 Round A, item 3) - ocupa el
                                    lugar de la primera fila de ítem de esta tabla. */}
                                {!(isReadOnly || isFieldsReadOnly) && !isAddingHere && (
                                  <TableRow>
                                    <TableCell colSpan={8} align="right">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={!formData.currentVersion?.price_list_id}
                                        title="Agregar Material"
                                        onClick={() => {
                                          // Bloqueo real (ver también el guard en
                                          // handleAddMaterial): sin lista de precios
                                          // elegida no hay picker que gatear ni precio
                                          // que resolver, así que ni siquiera se abre
                                          // la fila de alta inline (CLAUDE.md §4).
                                          if (!formData.currentVersion?.price_list_id) return;
                                          setSelectedEnvironmentIndex(actualEnvIndex);
                                          setMaterialDialog(true);
                                        }}
                                      >
                                        <Add fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                )}

                                {env.materials.length === 0 && !isAddingHere && (
                                  <TableRow>
                                    <TableCell colSpan={8} align="center">
                                      <Typography variant="body2" color="text.secondary">
                                        No hay materiales agregados
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}

                                {/* Filas de materiales */}
                                {env.materials.map((material, matIndex) => {
                                  // M2: lo que el usuario cargó, + el 10% de
                                  // desperdicio cuando ese toggle está activo (spec
                                  // de esta tarea) - estructura/in-situ no tienen m2.
                                  const m2Display = material.metros_cuadrados
                                    ? Math.round((material.con_desperdicio ? material.metros_cuadrados * 1.1 : material.metros_cuadrados) * 100) / 100
                                    : null;
                                  return (
                                    <TableRow key={`material-${actualEnvIndex}-${matIndex}`}>
                                      <TableCell>{material.codigo}</TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                          {material.es_in_situ ? (
                                            <Chip label="Manual" size="small" color="warning" />
                                          ) : material.product?.is_outlet ? (
                                            <Chip icon={<LocalOffer />} label="Outlet" size="small" color="warning" />
                                          ) : null}
                                          <Typography variant="body2" fontWeight="medium">
                                            {material.descripcion}
                                          </Typography>
                                          {/* Chip de Estructura solo para artículos de estructura (no el principal) */}
                                          {material.observaciones && material.observaciones.toLowerCase().includes('estructura para') && (
                                            <Chip label="Estructura" size="small" color="success" variant="outlined" />
                                          )}
                                          {/* Chip de Desperdicio solo para el principal */}
                                          {material.con_desperdicio && (!material.observaciones || !material.observaciones.toLowerCase().includes('estructura para')) && (
                                            <Chip label="Desperdicio" size="small" color="secondary" variant="outlined" />
                                          )}
                                        </Box>
                                        {material.observaciones && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {material.observaciones}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight="medium">
                                          {formatCurrency(material.precio_unitario, material.currency)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        {!(isReadOnly || isFieldsReadOnly) ? (
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={material.unidades}
                                            onChange={(e) => {
                                              const newValue = parseInt(e.target.value) || 1;
                                              if (newValue < 1) return; // Validación mínima

                                              setFormData(prev => {
                                                const newEnvironments = [...prev.environments];
                                                const materialsArr = [...newEnvironments[actualEnvIndex].materials];
                                                // Sincroniza m2 y recalcula las filas de
                                                // estructura de esta línea (item 4).
                                                materialsArr[matIndex] = syncM2ToUnidades(materialsArr[matIndex], newValue);
                                                const rebuilt = rebuildStructureRows(materialsArr, matIndex);
                                                newEnvironments[actualEnvIndex] = { ...newEnvironments[actualEnvIndex], materials: rebuilt };
                                                return { ...prev, environments: newEnvironments };
                                              });
                                              setIsDirty(true);
                                            }}
                                            sx={{ width: '90px' }}
                                            inputProps={{
                                              min: 1,
                                              style: { textAlign: 'right', padding: '8px' }
                                            }}
                                          />
                                        ) : (
                                          <Typography variant="body2" fontWeight="medium">
                                            {material.unidades}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {m2Display !== null ? `${m2Display} m²` : '-'}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                                          {formatCurrency(material.unidades * material.precio_unitario, material.currency)}
                                        </Typography>
                                      </TableCell>
                                      {/* Descuento de la línea (CLAUDE.md §8.7 outlet model):
                                          outlet -> prellenado, sólo hacia abajo (max = descuento
                                          de producto); no-outlet -> visible cuando el ambiente
                                          tiene "Aplicar descuento" tildado o la línea tiene %. */}
                                      <TableCell align="center">
                                        {(isOutletLine(material)
                                          ? (material.descuento_producto || 0) > 0
                                          : descuentoTildado || (material.descuento_linea || 0) > 0) && (
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={Math.round((material.descuento_linea || 0) * 10000) / 100}
                                            disabled={isReadOnly || isFieldsReadOnly}
                                            onChange={(e) => {
                                              const value = clampLineDiscount(material, Number(e.target.value));
                                              setFormData(prev => {
                                                const newEnvironments = [...prev.environments];
                                                newEnvironments[actualEnvIndex].materials[matIndex] = {
                                                  ...newEnvironments[actualEnvIndex].materials[matIndex], descuento_linea: value,
                                                };
                                                return { ...prev, environments: newEnvironments };
                                              });
                                              setIsDirty(true);
                                            }}
                                            inputProps={{
                                              min: 0,
                                              max: isOutletLine(material) ? Math.round((material.descuento_producto || 0) * 10000) / 100 : 99.99,
                                              step: 1,
                                              style: { width: '56px', textAlign: 'right', padding: '6px' },
                                            }}
                                            InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {!(isReadOnly || isFieldsReadOnly) && (
                                          <>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleEditMaterial(actualEnvIndex, matIndex)}
                                              color="primary"
                                              title="Editar"
                                            >
                                              <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleDeleteMaterial(actualEnvIndex, matIndex)}
                                              color="error"
                                              title="Eliminar"
                                            >
                                              <Delete fontSize="small" />
                                            </IconButton>
                                          </>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}

                                {/* Fila de alta inline de material (reemplaza el
                                    Dialog modal anterior - CLAUDE.md/tarea: "convert
                                    from a popup to an inline row"). Mismo contenido
                                    que tenía el Dialog, ahora embebido acá. */}
                                {isAddingHere && (
                                  <TableRow>
                                    <TableCell colSpan={8} sx={{ bgcolor: 'grey.50', verticalAlign: 'top' }}>
                                      <Stack spacing={2} sx={{ py: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                          {editingMaterialIndex ? 'Editar' : 'Agregar'} Material - {env.nombre}
                                        </Typography>

                                        <Autocomplete
                                          options={products}
                                          getOptionLabel={(option: Product) => `${option.codigo} ${option.sku ? `- ${option.sku}` : ''} ${option.color ? `(${option.color})` : ''}`}
                                          getOptionDisabled={(option: Product) => isProductGated(option)}
                                          value={!newMaterial.es_in_situ ? (newMaterial.product || null) : null}
                                          onChange={(event, newValue) => handleProductSelect(newValue)}
                                          disabled={newMaterial.es_in_situ}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              label="Seleccionar Producto *"
                                              placeholder="Buscar por descripción, código, color o grupo..."
                                              helperText={
                                                priceMap !== null
                                                  ? `Precios según ${myPriceLists.find(pl => pl.id === formData.currentVersion?.price_list_id)?.name || 'la lista elegida'} - los productos sin precio en esa lista aparecen deshabilitados.`
                                                  : undefined
                                              }
                                            />
                                          )}
                                          renderOption={(props: any, option: Product) => {
                                            const gated = isProductGated(option);
                                            return (
                                              <Box component="li" {...props}>
                                                <Box>
                                                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                      {option.descripcion || option.codigo}
                                                    </Typography>
                                                    {option.is_outlet && (
                                                      <Chip label="Outlet" size="small" color="warning" />
                                                    )}
                                                    {gated && (
                                                      <Chip
                                                        label={`Sin precio en ${myPriceLists.find(pl => pl.id === formData.currentVersion?.price_list_id)?.name || 'esta lista'}`}
                                                        size="small"
                                                        color="default"
                                                      />
                                                    )}
                                                  </Box>
                                                  <Typography variant="caption" color="text.secondary">
                                                    Código: {option.codigo} | Grupo: {option.grupo || 'Sin grupo'} | Color: {option.color || 'Sin color'} | Precio: {formatCurrency(gated ? 0 : (priceMap?.[option.id]?.price ?? 0))}
                                                  </Typography>
                                                  {option.largo && option.ancho && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                      Dimensiones: {option.largo} × {option.ancho} {option.espesor ? `× ${option.espesor}` : ''}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Box>
                                            );
                                          }}
                                          filterOptions={(options: Product[], { inputValue }: { inputValue: string }) => {
                                            if (!inputValue) return options;
                                            const lowerInput = inputValue.toLowerCase();
                                            return options.filter((option: Product) =>
                                              (option.descripcion && option.descripcion.toLowerCase().includes(lowerInput)) ||
                                              (option.grupo && option.grupo.toLowerCase().includes(lowerInput)) ||
                                              option.codigo.toLowerCase().includes(lowerInput) ||
                                              (option.color && option.color.toLowerCase().includes(lowerInput)) ||
                                              (option.sku && option.sku.toLowerCase().includes(lowerInput))
                                            );
                                          }}
                                        />

                                        {/* Toggle para producto manual (abajo del selector) */}
                                        <Box>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={newMaterial.es_in_situ}
                                                onChange={(e) => setNewMaterial(prev => ({
                                                  ...prev,
                                                  es_in_situ: e.target.checked,
                                                  product_id: undefined,
                                                  product: undefined,
                                                  descripcion: '',
                                                  codigo: '',
                                                  precio_unitario: 0,
                                                  cantidad_calculada: 0,
                                                  metros_cuadrados: 1,
                                                  usar_cantidad_manual: false,
                                                  con_desperdicio: true,
                                                  con_estructura: false,
                                                  observaciones: ''
                                                }))}
                                              />
                                            }
                                            label="Ingresar producto manual (no existente en catálogo)"
                                          />
                                          {newMaterial.es_in_situ && (
                                            <Alert severity="warning" sx={{ mt: 1 }}>
                                              Este producto no se creará en el Catálogo, solo aparecerá en este presupuesto
                                            </Alert>
                                          )}
                                        </Box>

                                        {!newMaterial.es_in_situ ? (
                                          // Selección de producto existente - opciones adicionales
                                          <>
                                            {newMaterial.product && (
                                              <>
                                                {/* Opciones de desperdicio y estructura */}
                                                <Box>
                                                  <Typography variant="subtitle2" gutterBottom>
                                                    Opciones del Producto:
                                                  </Typography>
                                                  <Stack spacing={1}>
                                                    <FormControlLabel
                                                      control={
                                                        <Checkbox
                                                          checked={newMaterial.con_desperdicio || false}
                                                          onChange={(e) => handleDesperdicioChange(e.target.checked)}
                                                        />
                                                      }
                                                      label="Incluir desperdicio (+10%)"
                                                    />
                                                    {newMaterial.product.estructura_m2 && (
                                                      <FormControlLabel
                                                        control={
                                                          <Checkbox
                                                            checked={newMaterial.con_estructura || false}
                                                            onChange={(e) => setNewMaterial(prev => ({
                                                              ...prev,
                                                              con_estructura: e.target.checked
                                                            }))}
                                                          />
                                                        }
                                                        label="Incluir estructura"
                                                      />
                                                    )}
                                                  </Stack>
                                                </Box>

                                                {/* Campos de m² y cantidad que se completan mutuamente */}
                                                <Box>
                                                  <Typography variant="subtitle2" gutterBottom>
                                                    Cálculo de Cantidad:
                                                  </Typography>
                                                  <Box display="flex" gap={2}>
                                                    <TextField
                                                      fullWidth
                                                      label="Metros Cuadrados"
                                                      type="number"
                                                      value={newMaterial.metros_cuadrados || 1}
                                                      onChange={(e) => {
                                                        if (!newMaterial.usar_cantidad_manual) {
                                                          handleMetrosCuadradosChange(parseFloat(e.target.value) || 1);
                                                        } else {
                                                          setNewMaterial(prev => ({
                                                            ...prev,
                                                            metros_cuadrados: parseFloat(e.target.value) || 1
                                                          }));
                                                        }
                                                      }}
                                                      placeholder="Área a cubrir en m²"
                                                      inputProps={{ min: 0.1, step: 0.1 }}
                                                    />

                                                    <TextField
                                                      fullWidth
                                                      label="Cantidad (unidades)"
                                                      type="number"
                                                      value={(newMaterial.usar_cantidad_manual ? newMaterial.cantidad_manual : newMaterial.cantidad_calculada) || ''}
                                                      onChange={(e) => {
                                                        const cantidad = parseFloat(e.target.value) || 0;
                                                        setNewMaterial(prev => ({ ...prev, usar_cantidad_manual: true }));
                                                        handleCantidadManualChange(cantidad);
                                                      }}
                                                      placeholder="Cantidad específica"
                                                      inputProps={{ min: 0, step: 1 }}
                                                    />
                                                  </Box>
                                                </Box>

                                                {newMaterial.cantidad_calculada && newMaterial.cantidad_calculada > 0 && !newMaterial.usar_cantidad_manual && (
                                                  <Alert severity="info">
                                                    <Typography variant="body2">
                                                      Cantidad calculada automáticamente: <strong>{newMaterial.cantidad_calculada} unidades</strong>
                                                      {newMaterial.con_desperdicio && <span> (incluye 10% desperdicio)</span>}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      Basado en {newMaterial.metros_cuadrados}m² × {newMaterial.product.factor_conversion_m2} unidades/m²
                                                    </Typography>
                                                  </Alert>
                                                )}

                                                {newMaterial.usar_cantidad_manual && newMaterial.cantidad_manual && newMaterial.product && (
                                                  <Alert severity="success">
                                                    <Typography variant="body2">
                                                      Metros cuadrados calculados automáticamente: <strong>{newMaterial.metros_cuadrados}m²</strong>
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                      Basado en {newMaterial.cantidad_manual} unidades ÷ {newMaterial.product.factor_conversion_m2} unidades/m²
                                                      {newMaterial.con_desperdicio && <span> (considerando desperdicio incluido)</span>}
                                                    </Typography>
                                                  </Alert>
                                                )}

                                                <FormControlLabel
                                                  control={
                                                    <Checkbox
                                                      checked={newMaterial.usar_cantidad_manual || false}
                                                      onChange={(e) => {
                                                        const useManual = e.target.checked;
                                                        setNewMaterial(prev => ({
                                                          ...prev,
                                                          usar_cantidad_manual: useManual,
                                                          unidades: useManual ? (prev.cantidad_manual || 0) : (prev.cantidad_calculada || 0)
                                                        }));
                                                      }}
                                                    />
                                                  }
                                                  label="Usar cantidad manual (deshabilita cálculo automático)"
                                                />

                                                {/* Campo de observaciones */}
                                                <TextField
                                                  fullWidth
                                                  label="Observaciones"
                                                  value={newMaterial.observaciones || ''}
                                                  onChange={(e) => setNewMaterial(prev => ({
                                                    ...prev,
                                                    observaciones: e.target.value.slice(0, 100)
                                                  }))}
                                                  placeholder="Observaciones sobre este material"
                                                  multiline
                                                  rows={2}
                                                  inputProps={{ maxLength: 100 }}
                                                  helperText={`${(newMaterial.observaciones || '').length}/100 caracteres`}
                                                />

                                                {/* Vista previa de estructura si está marcada */}
                                                {newMaterial.con_estructura && newMaterial.product.estructura_m2 && (
                                                  <Alert severity="info">
                                                    <Typography variant="subtitle2" gutterBottom>
                                                      ⚠️ Estructura incluida
                                                    </Typography>
                                                    <Typography variant="body2" gutterBottom>
                                                      {newMaterial.product.grupo === 'PERFIL'
                                                        ? `Este producto incluye materiales de estructura que se agregarán automáticamente calculados por unidad (total unidades: ${newMaterial.unidades || 1}).`
                                                        : `Este producto incluye materiales de estructura que se agregarán automáticamente calculados por metro cuadrado (total m²: ${newMaterial.metros_cuadrados || 1}).`}
                                                    </Typography>
                                                    <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                                                      {newMaterial.product.estructura_m2.split(/[;\n]+/).map((entry, index) => {
                                                        const trimmedEntry = entry.trim();
                                                        if (!trimmedEntry) return null;
                                                        try {
                                                          const [codigo, cantidadPorM2Str] = trimmedEntry.split(',');
                                                          const codigoTrimmed = codigo.trim();
                                                          const cantidadPorM2 = parseFloat(cantidadPorM2Str.trim());
                                                          const structureProduct = products.find((p: Product) => p.codigo === codigoTrimmed);
                                                          let cantidadBase, cantidadTotal, cantidadFinal, detalleCalculo;
                                                          if (newMaterial.product && newMaterial.product.grupo === 'PERFIL') {
                                                            cantidadBase = newMaterial.unidades || 1;
                                                            cantidadTotal = cantidadPorM2 * cantidadBase;
                                                            cantidadFinal = Math.ceil(cantidadTotal);
                                                            detalleCalculo = `(${cantidadPorM2}/unidad × ${cantidadBase} unidades)`;
                                                          } else {
                                                            cantidadBase = newMaterial.metros_cuadrados || 1;
                                                            cantidadTotal = cantidadPorM2 * cantidadBase;
                                                            cantidadFinal = Math.ceil(cantidadTotal);
                                                            detalleCalculo = `(${cantidadPorM2}/m² × ${cantidadBase}m²)`;
                                                          }
                                                          return (
                                                            <Typography
                                                              key={index}
                                                              component="li"
                                                              variant="caption"
                                                              color="text.secondary"
                                                            >
                                                              <strong>{codigoTrimmed}</strong>
                                                              {structureProduct && ` (${structureProduct.sku || 'Sin SKU'})`}:
                                                              {' '}{cantidadFinal} unidades
                                                              <span style={{ color: '#666' }}>
                                                                {' '}{detalleCalculo}
                                                              </span>
                                                              <span style={{ color: '#888', fontSize: '0.8em' }}>
                                                                {' '}(sin desperdicio)
                                                              </span>
                                                            </Typography>
                                                          );
                                                        } catch {
                                                          return null;
                                                        }
                                                      })}
                                                    </Box>
                                                  </Alert>
                                                )}
                                              </>
                                            )}
                                          </>
                                        ) : (
                                          // Creación de material in situ
                                          <>
                                            <TextField
                                              fullWidth
                                              label="Código *"
                                              value={newMaterial.codigo}
                                              onChange={(e) => setNewMaterial(prev => ({
                                                ...prev,
                                                codigo: e.target.value
                                              }))}
                                              placeholder="Código único del material"
                                              required
                                            />

                                            <TextField
                                              fullWidth
                                              label="Descripción *"
                                              value={newMaterial.descripcion}
                                              onChange={(e) => setNewMaterial(prev => ({
                                                ...prev,
                                                descripcion: e.target.value
                                              }))}
                                              placeholder="Descripción detallada del material"
                                              required
                                            />

                                            <Box display="flex" gap={2}>
                                              <TextField
                                                fullWidth
                                                label="Unidades"
                                                type="number"
                                                value={newMaterial.unidades || ''}
                                                onChange={(e) => setNewMaterial(prev => ({
                                                  ...prev,
                                                  unidades: parseFloat(e.target.value) || 0
                                                }))}
                                                placeholder="Cantidad"
                                              />

                                              <TextField
                                                fullWidth
                                                label="Precio Unitario"
                                                type="number"
                                                value={newMaterial.precio_unitario || ''}
                                                onChange={(e) => setNewMaterial(prev => ({
                                                  ...prev,
                                                  precio_unitario: parseFloat(e.target.value) || 0
                                                }))}
                                                placeholder="Precio por unidad"
                                              />

                                              <FormControl fullWidth>
                                                <InputLabel>Moneda</InputLabel>
                                                <Select
                                                  value={newMaterial.currency}
                                                  onChange={(e) => setNewMaterial(prev => ({
                                                    ...prev,
                                                    currency: e.target.value as 'USD' | 'ARS'
                                                  }))}
                                                  label="Moneda"
                                                >
                                                  <MenuItem value="USD">USD</MenuItem>
                                                  <MenuItem value="ARS">ARS</MenuItem>
                                                </Select>
                                              </FormControl>
                                            </Box>
                                          </>
                                        )}

                                        {/* Resumen del cálculo */}
                                        {(newMaterial.unidades > 0 && newMaterial.precio_unitario > 0) && (
                                          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                              Resumen del Material
                                            </Typography>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                              <Typography variant="body2">
                                                {newMaterial.unidades} unidades × {formatCurrency(newMaterial.precio_unitario)}
                                              </Typography>
                                              <Typography variant="h6" color="primary">
                                                {formatCurrency(newMaterial.unidades * newMaterial.precio_unitario)}
                                              </Typography>
                                            </Box>
                                            {newMaterial.product && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Producto: {newMaterial.product.codigo} {newMaterial.product.sku ? `(${newMaterial.product.sku})` : ''}
                                              </Typography>
                                            )}
                                          </Box>
                                        )}

                                        <Box display="flex" justifyContent="flex-end" gap={1}>
                                          <Button onClick={() => {
                                            setMaterialDialog(false);
                                            setSelectedEnvironmentIndex(null);
                                            setEditingMaterialIndex(null);
                                          }}>
                                            Cancelar
                                          </Button>
                                          <Button
                                            onClick={editingMaterialIndex ? handleUpdateMaterial : handleAddMaterial}
                                            variant="contained"
                                            disabled={
                                              newMaterial.es_in_situ
                                                ? (!newMaterial.codigo.trim() || !newMaterial.descripcion.trim())
                                                : (!newMaterial.product_id || (!!newMaterial.product && isProductGated(newMaterial.product)))
                                            }
                                          >
                                            {editingMaterialIndex ? 'Guardar Cambios' : 'Agregar Material'}
                                          </Button>
                                        </Box>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                )}

                                {/* Subtotal del ambiente (CLAUDE.md §8.7,
                                    2026-09-08): sólo el subtotal, con los
                                    descuentos de producto por línea ya
                                    aplicados. El descuento por ambiente se
                                    retiró - el descuento del rep es ahora un
                                    único % a nivel de sección (abajo). Sin
                                    total de Unidades (sumar unidades de
                                    productos distintos no es significativo). */}
                                {/* Descuento del ambiente: $ de los descuentos por línea
                                    (CLAUDE.md §8.7 outlet model). Oculto si es 0.
                                    Valor bajo la columna Total. */}
                                {descuentoProductosUsd > 0.005 && (
                                  <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.04)' }}>
                                    <TableCell colSpan={5} align="right">
                                      Descuento:
                                    </TableCell>
                                    <TableCell align="right">
                                      - {formatCurrency(descuentoProductosUsd, 'USD')}
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                  </TableRow>
                                )}
                                <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.04)' }}>
                                  <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>
                                    Subtotal Materiales {env.nombre}:
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    <Box>
                                      <Typography variant="body2">
                                        {formatCurrency(subtotalUsd, 'USD')}
                                      </Typography>
                                      {subtotalArs > 0 && (
                                        <Typography variant="body2">
                                          {formatCurrency(subtotalArs, 'AR')}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>

                        {/* Comentarios de este ambiente (CLAUDE.md §10 field report
                            #11) - solo si el ambiente ya tiene id real (existe en
                            el backend). Ya no es una fila de la tabla compartida -
                            cada ambiente tiene la suya propia. */}
                        {env.id && budget && currentVersion && (
                          <Box sx={{ mt: 1 }}>
                            <CommentThread
                              budgetId={budget.id}
                              versionId={currentVersion.id}
                              scopeType="ambiente"
                              scopeId={env.id}
                              comments={versionComments}
                              editable={commentsEditable}
                              onChange={updateComments}
                              title={`Comentarios - ${env.nombre}`}
                            />
                          </Box>
                        )}

                        {/* Línea divisoria entre ambientes */}
                        {envIndex < filteredEnvs.length - 1 && (
                          <Divider sx={{ borderBottomWidth: 2, borderColor: '#e0e0e0', mt: 3 }} />
                        )}
                      </Box>
                    );
                  })}

                {/* Footer de Materiales (CLAUDE.md §8.7 outlet model): sin descuento
                    de sección. Subtotal = suma de subtotales por ambiente (netos de
                    descuento por línea) = Total + IVA. Cliente-side (misma cuenta que
                    el servidor) para que reaccione en vivo al editar descuentos. */}
                {formData.environments.length > 0 && (
                  <Box display="flex" justifyContent="center">
                    <Box sx={{ maxWidth: 'calc(100% - 40px)', width: '100%' }}>
                      {(() => {
                        const usdLines = formData.environments
                          .filter(e => e.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                          .flatMap(e => e.materials)
                          .filter(m => m.currency === 'USD');
                        const brutoUsd = usdLines.reduce((t, m) => t + m.unidades * m.precio_unitario, 0);
                        const materialesNetoUsd = usdLines.reduce(
                          (t, m) => t + m.unidades * m.precio_unitario * (1 - (m.descuento_linea || 0)), 0);
                        const descuentoEfectivo = brutoUsd > 0 ? (1 - materialesNetoUsd / brutoUsd) * 100 : 0;
                        return (
                          <>
                            <Box sx={{ px: 3, py: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                              <Typography sx={{ fontWeight: 600 }}>Subtotal Materiales:</Typography>
                              <Typography sx={{ fontWeight: 600 }}>
                                {formatCurrency(materialesNetoUsd, 'USD')}
                              </Typography>
                            </Box>
                            {/* 4. Total (neto) + IVA - único con fondo (negro/blanco) */}
                            <Box sx={{ bgcolor: 'common.black', color: 'common.white', px: 3, py: 1, borderRadius: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                              <Typography sx={{ fontWeight: 600 }}>Total Materiales:</Typography>
                              <Typography sx={{ fontWeight: 600 }}>
                                {`${formatCurrency(materialesNetoUsd, 'USD')} + IVA`}
                              </Typography>
                            </Box>
                            {/* % de descuento efectivo (field report #12) sin obligar a calcularlo a mano */}
                            {descuentoEfectivo > 0.05 && (
                              <Box sx={{ px: 3, py: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Descuento efectivo: {descuentoEfectivo.toFixed(1)}%
                                </Typography>
                              </Box>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box display="flex" justifyContent="center">
                <Alert severity="info" sx={{ maxWidth: 'calc(100% - 40px)' }}>
                  No hay ambientes configurados. Agrega un ambiente para comenzar.
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Card de Instalación */}
        <Card>
          <CardHeader 
            title="Instalación"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<Build color="primary" />}
            subheader={`${formData.environments.filter(env => env.installations && env.installations.length > 0).length} ambiente(s) con instalación`}
            subheaderTypographyProps={{ variant: 'body1' }}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ p: 3 }}>
            {formData.environments.length > 0 ? (
              <Box>
                <Box display="flex" justifyContent="center">
                  <TableContainer component={Paper} sx={{ maxWidth: 'calc(100% - 40px)' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ambiente</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unidad</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio por Unidad</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Cantidad de Unidades</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* "+" inline en vez del botón "Agregar Instalación"
                            del header (Phase 8.7 Round A, item 3) - ocupa el
                            lugar de la primera fila de ítem de esta tabla. */}
                        {!(isReadOnly || isFieldsReadOnly) && !installationDialog && (
                          <TableRow>
                            <TableCell colSpan={7} align="right">
                              <IconButton
                                size="small"
                                color="primary"
                                title="Agregar Instalación"
                                onClick={() => {
                                  // Sin ambiente preseleccionado - se elige en
                                  // la fila inline que aparece abajo (antes
                                  // era un Dialog aparte).
                                  setSelectedEnvironmentIndex(null);
                                  setInstallationDialog(true);
                                }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                        {formData.environments
                          .filter(env => env.installations && env.installations.length > 0)
                          .map((env, envIndex) => {
                            // Índice REAL en formData.environments (el
                            // "envIndex" de este .filter().map() es la
                            // posición dentro del arreglo FILTRADO, no el
                            // índice real - se usa tal cual más abajo para no
                            // tocar el flujo de borrado existente, pero la
                            // edición nueva de este batch usa el real).
                            const actualEnvIndex = formData.environments.findIndex(e => e === env);
                            // Calcular área total del ambiente
                            const areaTotal = env.materials.reduce((total, material) => {
                              return total + (material.metros_cuadrados || 0);
                            }, 0);

                            // Renderizar una fila por cada instalación -
                            // moneda por fila restaurada (CLAUDE.md §8.5,
                            // 2026-09-01): catálogo usa installation_service
                            // .currency, 'custom' sigue siendo siempre USD.
                            return env.installations.map((installation, instIndex) => {
                              const installationCurrency = installation.installation_service
                                ? (installation.installation_service.currency || 'USD')
                                : 'USD';
                              let subtotal = 0;
                              if (installation.installation_service) {
                                const precio = installation.mano_obra_m2 || installation.installation_service.precio;
                                if (precio) {
                                  subtotal = precio * (installation.cantidad_manual ?? 1);
                                }
                              } else if (installation.mano_obra_m2) {
                                // Para instalaciones custom: si unidad es 'global', no multiplicar por cantidad
                                const esGlobal = installation.unidad?.toLowerCase() === 'global';
                                subtotal = esGlobal ? installation.mano_obra_m2 : installation.mano_obra_m2 * (installation.cantidad_manual ?? 1);
                              }
                              // PRECIO = 0 en el catálogo significa "Consultar"
                              // (CLAUDE.md §7) - no un precio real gratuito. Solo
                              // aplica sin un precio editado a mano en esta línea
                              // (un $0 editado a mano sigue siendo un $0 real,
                              // CLAUDE.md §6 permite edición manual en Mano de Obra).
                              const isConsultar = !!installation.installation_service &&
                                !installation.mano_obra_m2 &&
                                (installation.installation_service.precio || 0) === 0;

                              return (
                                <TableRow key={`installation-${envIndex}-${instIndex}`}>
                                  <TableCell>{env.nombre}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {installation?.installation_service
                                        ? `${installation.installation_service.descripcion}${installation.installation_service.complejidad ? ' - ' + installation.installation_service.complejidad : ''}`
                                        : installation?.trabajo || 'Instalación personalizada'}
                                    </Typography>
                                    {installation?.descripcion_trabajo && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {installation.descripcion_trabajo}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {/* Custom: usar installation.unidad, Catálogo: usar installation_service.unidad */}
                                    {installation?.installation_service_id
                                      ? (installation?.installation_service?.unidad || '')
                                      : (installation?.unidad || '')}
                                  </TableCell>
                                  <TableCell align="right">
                                    {isConsultar ? (
                                      'Consultar'
                                    ) : installation?.unidad?.toLowerCase() === 'global' && !installation?.installation_service ? (
                                      '-'
                                    ) : (
                                      formatInstallationCurrency(
                                        installation?.mano_obra_m2 || installation?.installation_service?.precio || 0, installationCurrency
                                      )
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {installation?.cantidad_manual || areaTotal.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {isConsultar ? 'Consultar' : formatInstallationCurrency(subtotal, installationCurrency)}
                                  </TableCell>
                                  <TableCell align="center">
                                    {!(isReadOnly || isFieldsReadOnly) && (
                                      <>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleEditInstallation(actualEnvIndex, instIndex)}
                                          color="primary"
                                          title="Editar"
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={async () => {
                                            try {
                                              setSaving(true);
                                              // Eliminar del backend si tiene ID
                                              if (installation.id && installation.id > 0) {
                                                await BudgetApiService.deleteEnvironmentInstallation(installation.id);
                                                console.log(`✅ Instalación eliminada del backend`);
                                              }
                                              // Eliminar del estado local por
                                              // REFERENCIA de ambiente (no por el
                                              // índice del arreglo FILTRADO por
                                              // installations.length > 0): con el
                                              // índice filtrado un ambiente
                                              // anterior sin instalaciones
                                              // desplazaba el borrado al ambiente
                                              // equivocado (field report #3, chip
                                              // task_5e3f9db9).
                                              setFormData(prev => ({
                                                ...prev,
                                                environments: removeInstallationByEnvRef(prev.environments, env, instIndex),
                                              }));
                                            } catch (error) {
                                              console.error('❌ Error eliminando instalación:', error);
                                              setError('Error al eliminar la instalación');
                                            } finally {
                                              setSaving(false);
                                            }
                                          }}
                                          color="error"
                                          title="Eliminar"
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            });
                        })}

                        {/* Fila inline para agregar una instalación - reemplaza
                            el Dialog "Agregar Instalación" que existía antes
                            (conversión a fila editable, 2026-09-05). Preserva
                            el toggle Catálogo/Personalizada, el picker de
                            installation_service, el fallback 'custom', y el
                            manejo de "Consultar" ($0 = precio a consultar).
                            Solo visible cuando no es solo-lectura, ya que el
                            botón "Agregar Instalación" que la abre también
                            está guardado por esa condición. */}
                        {installationDialog && !(isReadOnly || isFieldsReadOnly) && (() => {
                          const isCatalog = installationMode === 'catalog';
                          const rowCurrency = isCatalog
                            ? (newInstallation.installation_service?.currency || 'USD')
                            : 'USD';
                          const isConsultarRow = isCatalog &&
                            !!newInstallation.installation_service &&
                            !newInstallation.mano_obra_m2 &&
                            (newInstallation.installation_service.precio || 0) === 0;
                          const rowPrecio = isCatalog
                            ? calculateInstallationPrice()
                            : (newInstallation.mano_obra_m2 || 0);
                          const esGlobalRow = !isCatalog && newInstallation.unidad?.toLowerCase() === 'global';
                          const rowTotal = esGlobalRow ? rowPrecio : rowPrecio * (newInstallation.cantidad_manual || 0);
                          const disableSave =
                            selectedEnvironmentIndex === null ||
                            (newInstallation.cantidad_manual ?? 0) <= 0 ||
                            (isCatalog && !newInstallation.installation_service_id) ||
                            (!isCatalog && (!newInstallation.trabajo?.trim() || !newInstallation.mano_obra_m2));

                          return (
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                              <TableCell sx={{ minWidth: 180, verticalAlign: 'top' }}>
                                <FormControl fullWidth size="small" required>
                                  <InputLabel shrink>Ambiente</InputLabel>
                                  <Select
                                    label="Ambiente"
                                    displayEmpty
                                    notched
                                    value={selectedEnvironmentIndex !== null ? selectedEnvironmentIndex : ''}
                                    onChange={(e) => setSelectedEnvironmentIndex(Number(e.target.value))}
                                  >
                                    <MenuItem value="" disabled>
                                      <em>Elegir...</em>
                                    </MenuItem>
                                    <MenuItem value={GENERAL_INSTALLATIONS_INDEX}>
                                      <em>Instalaciones Generales</em>
                                    </MenuItem>
                                    {formData.environments
                                      .filter(env => env.nombre !== GENERAL_INSTALLATIONS_ENV_NAME)
                                      .map((env) => {
                                        const realIndex = formData.environments.findIndex(e => e.nombre === env.nombre);
                                        return (
                                          <MenuItem key={realIndex} value={realIndex}>
                                            {env.nombre}
                                          </MenuItem>
                                        );
                                      })}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell sx={{ minWidth: 280, verticalAlign: 'top' }}>
                                <Stack spacing={1}>
                                  <ToggleButtonGroup
                                    value={installationMode}
                                    exclusive
                                    size="small"
                                    onChange={(e, newMode) => {
                                      if (newMode !== null) {
                                        setInstallationMode(newMode);
                                        setNewInstallation({ cantidad_manual: 1 });
                                      }
                                    }}
                                  >
                                    <ToggleButton value="catalog">Catálogo</ToggleButton>
                                    <ToggleButton value="custom">Personalizada</ToggleButton>
                                  </ToggleButtonGroup>
                                  {isCatalog ? (
                                    <Autocomplete
                                      size="small"
                                      options={installationServices}
                                      getOptionLabel={(option: any) =>
                                        `${option.descripcion}${option.complejidad ? ' - ' + option.complejidad : ''} (${option.unidad})`
                                      }
                                      value={newInstallation.installation_service || null}
                                      onChange={(event, newValue) => handleInstallationServiceSelect(newValue)}
                                      loading={loadingInstallationServices}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Servicio de Instalación *"
                                          placeholder="Buscar por código, descripción..."
                                          InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                              <>
                                                {loadingInstallationServices ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                              </>
                                            ),
                                          }}
                                        />
                                      )}
                                      renderOption={(props: any, option: any) => (
                                        <Box component="li" {...props}>
                                          <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                              {option.descripcion}{option.complejidad ? ` - ${option.complejidad}` : ''}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Unidad: {option.unidad} | Precio: {option.precio === 0 ? 'Consultar' : formatInstallationCurrency(option.precio, option.currency || 'USD')}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      )}
                                      filterOptions={(options: any[], { inputValue }: { inputValue: string }) => {
                                        if (!inputValue) return options;
                                        const lowerInput = inputValue.toLowerCase();
                                        return options.filter((option: any) =>
                                          option.descripcion.toLowerCase().includes(lowerInput) ||
                                          (option.codigo && option.codigo.toLowerCase().includes(lowerInput)) ||
                                          (option.complejidad && option.complejidad.toLowerCase().includes(lowerInput)) ||
                                          option.unidad.toLowerCase().includes(lowerInput)
                                        );
                                      }}
                                    />
                                  ) : (
                                    <>
                                      <TextField
                                        size="small"
                                        label="Nombre del trabajo *"
                                        value={newInstallation.trabajo || ''}
                                        onChange={(e) => setNewInstallation(prev => ({
                                          ...prev,
                                          trabajo: e.target.value,
                                          tipo_instalacion: 'custom'
                                        }))}
                                        placeholder="Ej: Instalación de vidrio templado"
                                      />
                                      <FormControl size="small">
                                        <InputLabel>Unidad</InputLabel>
                                        <Select
                                          label="Unidad"
                                          value={newInstallation.unidad || ''}
                                          onChange={(e) => setNewInstallation(prev => ({
                                            ...prev,
                                            unidad: e.target.value
                                          }))}
                                        >
                                          <MenuItem value="m2">m2</MenuItem>
                                          <MenuItem value="ml">ml (metro lineal)</MenuItem>
                                          <MenuItem value="unit">Unidad</MenuItem>
                                          <MenuItem value="global">Global</MenuItem>
                                          <MenuItem value="hora">Hora</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </>
                                  )}
                                  <TextField
                                    size="small"
                                    label="Nota (opcional)"
                                    value={newInstallation.descripcion_trabajo || ''}
                                    onChange={(e) => setNewInstallation(prev => ({
                                      ...prev,
                                      descripcion_trabajo: e.target.value
                                    }))}
                                    placeholder="Descripción del trabajo"
                                  />
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ verticalAlign: 'top' }}>
                                {isCatalog
                                  ? (newInstallation.installation_service?.unidad || '')
                                  : (newInstallation.unidad || '')}
                              </TableCell>
                              <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  sx={{ width: 130 }}
                                  /* Phase 8.7 Round A, item 5: `||` en vez de `??` -
                                     el reset post-alta deja mano_obra_m2 en 0 (no
                                     null/undefined), así que `??` no lo capturaba y
                                     se veía un "0" prellenado en modo personalizada. */
                                  value={newInstallation.mano_obra_m2 || (isCatalog ? calculateInstallationPrice() : '')}
                                  onChange={(e) => setNewInstallation(prev => ({
                                    ...prev,
                                    mano_obra_m2: parseFloat(e.target.value) || 0
                                  }))}
                                  InputProps={{
                                    endAdornment: <span style={{ marginLeft: '4px', color: '#666', fontSize: '0.75rem' }}>{rowCurrency}</span>
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  sx={{ width: 90 }}
                                  value={newInstallation.cantidad_manual}
                                  onChange={(e) => setNewInstallation(prev => ({
                                    ...prev,
                                    cantidad_manual: parseFloat(e.target.value) || 1
                                  }))}
                                  inputProps={{ min: 0.1, step: 0.1 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                {isConsultarRow ? 'Consultar' : formatInstallationCurrency(rowTotal, rowCurrency)}
                              </TableCell>
                              <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={editingInstallationIndex ? handleUpdateInstallation : handleAddInstallation}
                                  disabled={disableSave}
                                  title={editingInstallationIndex ? 'Guardar Cambios' : 'Agregar'}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setInstallationDialog(false);
                                    setSelectedEnvironmentIndex(null);
                                    setInstallationMode('catalog');
                                    setNewInstallation({ cantidad_manual: 1 });
                                    setEditingInstallationIndex(null);
                                  }}
                                  title="Cancelar"
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })()}

                        {/* Footer unificado (walkthrough PO 2026-09-11): mismo
                            formato que Materiales. Diferencias de MO: sin
                            ambientes, sin descuento por producto, y SIN "+ IVA"
                            (MO no lleva IVA - CLAUDE §6/§8.9). Orden: Subtotal
                            (bruto) -> Descuento (%) input -> Descuento (monto,
                            oculto si %=0) -> Total (neto) fondo negro/blanco.
                            Fuente única: GET .../summary, convertido con la
                            cotización YA congelada de esta versión. No se cambia
                            ningún cálculo. */}
                        {/* 1. Subtotal Instalación (bruto, antes del descuento) */}
                        <TableRow>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                            Subtotal Instalación:
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 600 }}>
                            {versionSummary ? (
                              <>
                                {formatInstallationCurrency(Number(versionSummary.mano_obra_subtotal_local), versionSummary.mano_obra_currency)}
                                {versionSummary.fx_rate_used != null && (
                                  <Typography variant="caption" component="div" sx={{ opacity: 0.75 }}>
                                    Cotización: {Number(versionSummary.fx_rate_used).toFixed(2)}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <CircularProgress size={16} />
                            )}
                          </TableCell>
                        </TableRow>

                        {/* 2. Input de descuento de sección (siempre visible - es la
                            forma de setear el %; el monto va en su propia línea) */}
                        <TableRow>
                          <TableCell colSpan={7} align="right">
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                              <Typography sx={{ fontWeight: 600 }}>Descuento Instalación (%)</Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={formData.descuento_instalacion ? Math.round(formData.descuento_instalacion) : ''}
                                onChange={handleChangeDescuento('descuento_instalacion')}
                                inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                                disabled={(isReadOnly|| isFieldsReadOnly)}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* 3. Descuento (monto = bruto - neto) - oculto si % es 0
                            (CLAUDE.md §8.7 - nunca una línea de "$0 descuento"). */}
                        {versionSummary && Number(versionSummary.mano_obra_descuento_pct) > 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                              Descuento:
                            </TableCell>
                            <TableCell colSpan={2} align="right" sx={{ fontWeight: 600 }}>
                              {`- ${formatInstallationCurrency(Number(versionSummary.mano_obra_descuento_local), versionSummary.mano_obra_currency)}`}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* 4. Total Instalación (neto) - fondo negro/blanco, SIN "+ IVA" */}
                        <TableRow sx={{ backgroundColor: 'common.black' }}>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 600, color: 'common.white' }}>
                            Total Instalación:
                          </TableCell>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 600, color: 'common.white' }}>
                            {versionSummary
                              ? formatInstallationCurrency(Number(versionSummary.mano_obra_total_local), versionSummary.mano_obra_currency)
                              : <CircularProgress size={16} sx={{ color: 'common.white' }} />}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center">
                <Alert severity="info" sx={{ maxWidth: 'calc(100% - 40px)' }}>
                  No hay servicios de instalación configurados. Agregue instalación a los ambientes según sea necesario.
                </Alert>
              </Box>
            )}

            {/* Comentarios de Mano de Obra (CLAUDE.md §10 field report #11) -
                sección única por versión, no por ambiente. */}
            {budget && currentVersion && (
              <Box display="flex" justifyContent="center">
                <Box sx={{ width: 'calc(100% - 40px)' }}>
                  <CommentThread
                    budgetId={budget.id}
                    versionId={currentVersion.id}
                    scopeType="mano_de_obra"
                    comments={versionComments}
                    editable={commentsEditable}
                    onChange={updateComments}
                    title="Comentarios - Mano de Obra"
                  />
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Card de Fletes */}
        <Card>
          <CardHeader 
            title="Fletes y Envíos"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<LocalShipping color="primary" />}
            subheader={`${formData.freights.length} flete(s) configurado(s)`}
            subheaderTypographyProps={{ variant: 'body1' }}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ p: 3 }}>
            {versionSummary && !versionSummary.flete_habilitado ? (
              <Box display="flex" justifyContent="center">
                <Alert severity="info" sx={{ maxWidth: 'calc(100% - 40px)' }}>
                  La sección de Fletes está desactivada para este país (configuración de país, CLAUDE.md §6).
                </Alert>
              </Box>
            ) : (
              <Box>
                <Box display="flex" justifyContent="center">
                  <TableContainer component={Paper} sx={{ maxWidth: 'calc(100% - 40px)' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Monto</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Bonificado</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* "+" inline en vez del botón "Agregar Flete" del
                            header (Phase 8.7 Round A, item 3) - ocupa el
                            lugar de la primera fila de ítem de esta tabla. */}
                        {!(isReadOnly || isFieldsReadOnly) && !freightDialog && (
                          <TableRow>
                            <TableCell colSpan={4} align="right">
                              <IconButton
                                size="small"
                                color="primary"
                                title="Agregar Flete"
                                onClick={() => setFreightDialog(true)}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                        {formData.freights.map((freight, index) => (
                          <TableRow key={`freight-${index}`}>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="medium">
                                {freight.freight_rate?.descripcion || freight.tipo_envio}
                              </Typography>
                              {freight.codigo && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Código: {freight.codigo}
                                </Typography>
                              )}
                              {!(isReadOnly || isFieldsReadOnly) ? (
                                <TextField
                                  size="small"
                                  value={freight.descripcion_detalle || ''}
                                  onChange={(e) => {
                                    const newFreights = [...formData.freights];
                                    newFreights[index] = { ...newFreights[index], descripcion_detalle: e.target.value };
                                    setFormData(prev => ({ ...prev, freights: newFreights }));
                                  }}
                                  placeholder="Nota (opcional)"
                                  inputProps={{ maxLength: 150 }}
                                  sx={{ minWidth: 200, mt: 0.5 }}
                                />
                              ) : freight.descripcion_detalle ? (
                                <Typography variant="body2" color="text.secondary" display="block">
                                  {freight.descripcion_detalle}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="center">
                              {/* Bonificado (CLAUDE.md §6): se le cobra $0 al
                                  cliente, pero el monto interno real se
                                  conserva - se muestra apagado/tachado acá,
                                  nunca se oculta ni se reemplaza (eso es
                                  trabajo del PDF, no de esta pantalla). */}
                              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color={freight.bonificado ? 'text.disabled' : 'primary.main'}
                                  sx={freight.bonificado ? { textDecoration: 'line-through' } : undefined}
                                >
                                  {formatCurrency(freight.monto, freight.currency === 'ARS' ? 'AR' : freight.currency)}
                                  {freight.tipo === 'porcentaje' && '%'}
                                </Typography>
                                {freight.bonificado && (
                                  <Chip size="small" label="Bonificado" color="default" variant="outlined" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={!!freight.bonificado}
                                disabled={isReadOnly || isFieldsReadOnly}
                                onChange={(e) => {
                                  const newFreights = [...formData.freights];
                                  newFreights[index] = { ...newFreights[index], bonificado: e.target.checked };
                                  setFormData(prev => ({ ...prev, freights: newFreights }));
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {!(isReadOnly || isFieldsReadOnly) && (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditFreight(index)}
                                    color="primary"
                                    title="Editar"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteFreight(index)}
                                    color="error"
                                    title="Eliminar"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Fila inline para agregar un flete - reemplaza el
                            Dialog "Flete" que existía antes (conversión a
                            fila editable, 2026-09-05). Preserva el toggle
                            Catálogo/Manual, el picker de FreightRate y el
                            threading de freight_rate_id/codigo. Solo visible
                            cuando no es solo-lectura (mismo guard que el
                            botón "Agregar Flete" que la abre). */}
                        {freightDialog && !(isReadOnly || isFieldsReadOnly) && (
                          <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell sx={{ minWidth: 280, verticalAlign: 'top' }}>
                              <Stack spacing={1}>
                                <ToggleButtonGroup
                                  value={freightMode}
                                  exclusive
                                  size="small"
                                  onChange={(e, newMode) => {
                                    if (newMode !== null) {
                                      setFreightMode(newMode);
                                      setNewFreight({
                                        tipo_envio: '',
                                        monto: 0,
                                        tipo: 'fijo',
                                        currency: 'ARS',
                                        bonificado: false
                                      });
                                    }
                                  }}
                                >
                                  <ToggleButton value="catalog">Catálogo</ToggleButton>
                                  <ToggleButton value="manual">Manual</ToggleButton>
                                </ToggleButtonGroup>
                                {freightMode === 'catalog' ? (
                                  <>
                                    <Autocomplete
                                      size="small"
                                      options={freightRates}
                                      getOptionLabel={(option: FreightRate) =>
                                        `${option.zone}${option.descripcion ? ' - ' + option.descripcion : ''}${option.codigo ? ' (' + option.codigo + ')' : ''}`
                                      }
                                      value={newFreight.freight_rate || null}
                                      onChange={(event, newValue) => handleFreightRateSelect(newValue)}
                                      loading={loadingFreightRates}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Zona / Tarifa *"
                                          placeholder="Buscar por zona, código o descripción..."
                                          InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                              <>
                                                {loadingFreightRates ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                              </>
                                            ),
                                          }}
                                        />
                                      )}
                                      renderOption={(props: any, option: FreightRate) => (
                                        <Box component="li" {...props}>
                                          <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                              {option.zone}{option.descripcion ? ` - ${option.descripcion}` : ''}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Hasta {option.peso_max} kg / {option.m2_max} m² | Precio: {formatInstallationCurrency(option.price, option.currency || 'ARS')}
                                              {option.codigo ? ` | Código: ${option.codigo}` : ''}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      )}
                                      filterOptions={(options: FreightRate[], { inputValue }: { inputValue: string }) => {
                                        if (!inputValue) return options;
                                        const lowerInput = inputValue.toLowerCase();
                                        return options.filter((option: FreightRate) =>
                                          option.zone.toLowerCase().includes(lowerInput) ||
                                          (option.codigo && option.codigo.toLowerCase().includes(lowerInput)) ||
                                          (option.descripcion && option.descripcion.toLowerCase().includes(lowerInput))
                                        );
                                      }}
                                    />
                                    {newFreight.freight_rate && (
                                      <Typography variant="caption" color="text.secondary">
                                        Capacidad: hasta {newFreight.freight_rate.peso_max} kg / {newFreight.freight_rate.m2_max} m²
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <TextField
                                    size="small"
                                    label="Tipo de flete *"
                                    value={newFreight.tipo_envio}
                                    onChange={(e) => setNewFreight(prev => ({ ...prev, tipo_envio: e.target.value }))}
                                    placeholder="Ej: Flete y acarreo especial"
                                  />
                                )}
                                <TextField
                                  size="small"
                                  label="Nota (opcional)"
                                  value={newFreight.descripcion_detalle || ''}
                                  onChange={(e) => setNewFreight(prev => ({
                                    ...prev,
                                    descripcion_detalle: e.target.value
                                  }))}
                                  placeholder="Detalles del flete"
                                  inputProps={{ maxLength: 150 }}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                              <TextField
                                size="small"
                                type="number"
                                sx={{ width: 130 }}
                                value={newFreight.monto || ''}
                                onChange={(e) => setNewFreight(prev => ({ ...prev, monto: parseFloat(e.target.value) || 0 }))}
                                InputProps={{
                                  endAdornment: <span style={{ marginLeft: '4px', color: '#666', fontSize: '0.75rem' }}>{newFreight.currency === 'USD' ? 'U$' : 'AR$'}</span>
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'top', color: 'text.disabled' }}>
                              {/* Bonificado se decide después de agregada la
                                  línea (checkbox por fila, arriba) - no
                                  forma parte del alta inline. */}
                              -
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={editingFreightIndex !== null ? handleUpdateFreight : handleAddFreight}
                                disabled={freightMode === 'catalog' ? !newFreight.freight_rate_id : !newFreight.tipo_envio.trim()}
                                title={editingFreightIndex !== null ? 'Guardar Cambios' : 'Agregar'}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setFreightDialog(false);
                                  setFreightMode('catalog');
                                  setNewFreight({
                                    tipo_envio: '',
                                    monto: 0,
                                    tipo: 'fijo',
                                    currency: 'ARS',
                                    bonificado: false
                                  });
                                  setEditingFreightIndex(null);
                                }}
                                title="Cancelar"
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}

                      {/* Footer unificado (walkthrough PO 2026-09-11): mismo
                          formato que Materiales. Diferencias de Flete: sin
                          ambientes, sin descuento por producto, y SIN "+ IVA"
                          (Flete no lleva IVA - CLAUDE §6/§8.9). Orden: Subtotal
                          (bruto, neto de líneas Bonificado) -> Descuento (%)
                          input -> Descuento (monto, oculto si %=0) -> Total (neto)
                          fondo negro/blanco. El costo interno real (con Bonificado)
                          queda como referencia, nunca de cara al cliente. Fuente
                          única: GET .../summary. No se cambia ningún cálculo. */}
                      {/* 1. Subtotal Fletes (bruto, antes del descuento de sección) */}
                      <TableRow>
                        <TableCell colSpan={1} align="right" sx={{ fontWeight: 600 }}>
                          Subtotal Fletes:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {versionSummary ? (
                            <>
                              {formatCurrency(Number(versionSummary.flete_subtotal_cliente), versionSummary.flete_currency === 'ARS' ? 'AR' : versionSummary.flete_currency)}
                              {Number(versionSummary.flete_total_interno) !== Number(versionSummary.flete_subtotal_cliente) && (
                                <Tooltip title="Costo interno real, antes de aplicar Bonificado - solo para referencia de reps/admins, nunca se cobra ni se muestra al cliente">
                                  <Typography variant="caption" component="div" sx={{ opacity: 0.75 }}>
                                    Costo interno: {formatCurrency(Number(versionSummary.flete_total_interno), versionSummary.flete_currency === 'ARS' ? 'AR' : versionSummary.flete_currency)}
                                  </Typography>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <CircularProgress size={16} />
                          )}
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>

                      {/* 2. Input de descuento de sección */}
                      <TableRow>
                        <TableCell colSpan={2} align="right">
                          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                            <Typography sx={{ fontWeight: 600 }}>Descuento Fletes (%)</Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={formData.descuento_fletes ? Math.round(formData.descuento_fletes) : ''}
                              onChange={handleChangeDescuento('descuento_fletes')}
                              inputProps={{ min: 0, max: 100, step: 5, style: { width: '60px', textAlign: 'right' } }}
                              disabled={(isReadOnly || isFieldsReadOnly)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>

                      {/* 3. Descuento (monto = subtotal - total) - oculto si % es 0.
                          Restaurado (walkthrough PO 2026-09-12 r3): simétrico con
                          Mano de Obra, revierte la remoción Flete-only del r2. */}
                      {versionSummary && Number(versionSummary.flete_descuento_pct) > 0 && (
                        <TableRow>
                          <TableCell colSpan={1} align="right" sx={{ fontWeight: 600 }}>
                            Descuento:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {`- ${formatCurrency(Number(versionSummary.flete_subtotal_cliente) - Number(versionSummary.flete_total_cliente), versionSummary.flete_currency === 'ARS' ? 'AR' : versionSummary.flete_currency)}`}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                      )}

                      {/* Total Fletes (neto) - fondo negro/blanco, SIN "+ IVA" */}
                      <TableRow sx={{ backgroundColor: 'common.black' }}>
                        <TableCell colSpan={1} align="right" sx={{ fontWeight: 600, color: 'common.white' }}>
                          Total Fletes:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'common.white' }}>
                          {versionSummary
                            ? formatCurrency(Number(versionSummary.flete_total_cliente), versionSummary.flete_currency === 'ARS' ? 'AR' : versionSummary.flete_currency)
                            : <CircularProgress size={16} sx={{ color: 'common.white' }} />}
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              </Box>
            )}

            {/* Comentarios de Flete (CLAUDE.md §10 field report #11) -
                sección única por versión. */}
            {budget && currentVersion && (
              <Box display="flex" justifyContent="center">
                <Box sx={{ width: 'calc(100% - 40px)' }}>
                  <CommentThread
                    budgetId={budget.id}
                    versionId={currentVersion.id}
                    scopeType="flete"
                    comments={versionComments}
                    editable={commentsEditable}
                    onChange={updateComments}
                    title="Comentarios - Flete"
                  />
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Card de Condiciones Adicionales */}
        <Card>
          <CardHeader 
            title="Condiciones Adicionales"
            titleTypographyProps={{ variant: 'h6' }}
            avatar={<Assignment color="primary" />}
          />
          <CardContent>
            <TextField
              fullWidth
              label="Condiciones Adicionales"
              multiline
              rows={8}
              value={formData.currentVersion?.condiciones_adicionales || ''}
              onChange={(e) => {
                if (isReadOnly || isFieldsReadOnly) return;
                setFormData(prev => ({
                  ...prev,
                  currentVersion: {
                    ...prev.currentVersion!,
                    condiciones_adicionales: e.target.value
                  }
                }));
              }}
              placeholder="Observaciones adicionales"
              sx={{ mb: 2 }}
              InputProps={{ readOnly: isReadOnly || isFieldsReadOnly }}
            />
            
            <Box display="flex" justifyContent="center" sx={{ mx: 3 }}>
              <Button 
                variant="outlined" 
                onClick={() => setShowCondiciones(!showCondiciones)}
                startIcon={showCondiciones ? <ExpandMore /> : <Receipt />}
              >
                {showCondiciones ? 'Ocultar Condiciones Obligatorias de Contratación' : 'Ver Condiciones Obligatorias de Contratación'}
              </Button>
            </Box>
            
            {showCondiciones && currentVersion?.condiciones_contratacion && (
              <Box mt={2} sx={{ mx: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Condiciones Obligatorias de Contratación:</Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                    {currentVersion.condiciones_contratacion}
                  </Typography>
                </Paper>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Card de Resumen de Totales por Sección */}
        <Card>
          <CardHeader 
            title="Resumen Financiero"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<Receipt color="primary" />}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ p: 3 }}>
            {/* Mismos tres números que ya se muestran en cada sección de
                arriba (GET .../summary, CLAUDE.md §6/§10 field report #6) -
                esta card es solo una vista condensada, no un cálculo
                aparte. Nunca se suman entre sí (§6: no hay total combinado
                entre monedas). */}
            <Box display="flex" flexWrap="wrap" gap={4} sx={{ mx: 3 }}>
              <Box flex="1 1 250px">
                <Box textAlign="center" p={3} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="body1" color="text.secondary" gutterBottom fontWeight="medium">
                    Total Materiales (USD + IVA)
                  </Typography>
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    {versionSummary ? formatCurrency(Number(versionSummary.materiales_total_con_iva), 'USD') : <CircularProgress size={16} />}
                  </Typography>
                  {versionSummary && (
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Sin IVA: {formatCurrency(Number(versionSummary.materiales_usd), 'USD')}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box flex="1 1 250px">
                <Box textAlign="center" p={3} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="body1" color="text.secondary" gutterBottom fontWeight="medium">
                    Total Mano de Obra
                  </Typography>
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    {versionSummary
                      ? formatInstallationCurrency(Number(versionSummary.mano_obra_total_local), versionSummary.mano_obra_currency)
                      : <CircularProgress size={16} />}
                  </Typography>
                  {versionSummary && Number(versionSummary.mano_obra_descuento_pct) > 0 && (
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Sin descuento: {formatInstallationCurrency(Number(versionSummary.mano_obra_subtotal_local), versionSummary.mano_obra_currency)}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box flex="1 1 250px">
                <Box textAlign="center" p={3} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="body1" color="text.secondary" gutterBottom fontWeight="medium">
                    Total Flete
                  </Typography>
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    {versionSummary
                      ? formatCurrency(Number(versionSummary.flete_total_cliente), versionSummary.flete_currency === 'ARS' ? 'AR' : versionSummary.flete_currency)
                      : <CircularProgress size={16} />}
                  </Typography>
                  {versionSummary && Number(versionSummary.flete_descuento_pct) > 0 && (
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Sin descuento: {formatCurrency(Number(versionSummary.flete_subtotal_cliente), versionSummary.flete_currency === 'ARS' ? 'AR' : versionSummary.flete_currency)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Card de Historial de Versiones */}
        <Card>
          <CardHeader 
            title="Historial de Versiones"
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            avatar={<AccountBox color="primary" />}
            sx={{ pb: 1 }}
            /* Item E: se quitó el botón "Nueva Versión" duplicado de acá; la
               acción vive sólo en la barra de acciones de arriba (visible para
               versiones sent/sold/lost). */
          />
          <CardContent sx={{ p: 3 }}>
            {versions.length > 0 ? (
              <Box display="flex" justifyContent="center">
                <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 'calc(100% - 40px)' }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'primary.main' }}>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Versión</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Título</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Vendedor</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Fecha Creación</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.map((version) => {
                      // Bloqueada una vez sent/sold/lost, misma regla que
                      // isFieldsReadOnly usa para la versión vigente (CLAUDE.md
                      // §5) - acá evaluada por versión, no solo por la actual.
                      const versionNameLocked = ['sent', 'sold', 'lost'].includes(version.estado);
                      return (
                      <TableRow key={version.id}>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                            <Typography variant="body2" fontWeight="medium">
                              {version.numero_version || `v${version.version_number || version.id}`}
                            </Typography>
                            {version.is_current && <CheckCircle color="success" fontSize="medium" />}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {editingVersionListId === version.id ? (
                            <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                              <TextField
                                size="small"
                                value={editingVersionListName}
                                onChange={(e) => setEditingVersionListName(e.target.value)}
                                autoFocus
                                disabled={savingVersionListName}
                                inputProps={{ maxLength: 100 }}
                                sx={{ minWidth: 160 }}
                              />
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSaveVersionListName(version.id)}
                                disabled={savingVersionListName || !editingVersionListName.trim()}
                              >
                                <CheckCircle />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={handleCancelEditVersionListName}
                                disabled={savingVersionListName}
                              >
                                <Cancel />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                              <Typography variant="body2" fontWeight="medium">
                                {version.nombre_version || `Versión ${version.version_number}`}
                              </Typography>
                              {!versionNameLocked && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEditVersionListName(version.id, version.nombre_version)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {version.titulo}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {version.vendedor || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {version.fecha_creacion ? new Date(version.fecha_creacion).toLocaleDateString() : 
                             (version.created_at ? new Date(version.created_at).toLocaleDateString() : '-')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={version.is_current ? 'Activa' : 'Inactiva'}
                            color={version.is_current ? 'success' : 'default'}
                            variant={version.is_current ? 'filled' : 'outlined'}
                            icon={version.is_current ? <CheckCircle /> : undefined}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="Descargar PDF">
                              <IconButton 
                                size="small"
                                onClick={async () => {
                                  try {
                                    // Guardar cambios primero
                                    await updateAllBudgetData();
                                    // Luego descargar la versión específica
                                    await handleDownloadPdf(version.id);
                                  } catch (error) {
                                    setError('Error al guardar cambios o descargar PDF.');
                                  }
                                }}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center">
                <Alert severity="info" sx={{ maxWidth: 'calc(100% - 40px)' }}>No hay versiones disponibles</Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Diálogos - Reutilizando componentes del BudgetFormNew */}
      
      {/* Dialog para nuevo ambiente */}
      <Dialog
        open={environmentDialog}
        onClose={() => setEnvironmentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nuevo Ambiente</DialogTitle>
        <DialogContent
          onKeyDown={(e) => {
            // Phase 8.7 Round A, item 6: Enter dispara la acción por defecto -
            // salvo en el textarea de Descripción, donde Enter debe seguir
            // insertando un salto de línea.
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA' && newEnvironment.nombre.trim()) {
              e.preventDefault();
              handleAddEnvironment();
            }
          }}
        >
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nombre del Ambiente *"
              value={newEnvironment.nombre}
              onChange={(e) => setNewEnvironment(prev => ({
                ...prev,
                nombre: e.target.value
              }))}
              placeholder="Ej: Living, Cocina, Dormitorio"
            />
            
            <TextField
              fullWidth
              label="Descripción"
              value={newEnvironment.descripcion}
              onChange={(e) => setNewEnvironment(prev => ({
                ...prev,
                descripcion: e.target.value
              }))}
              placeholder="Descripción detallada del ambiente"
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvironmentDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddEnvironment}
            variant="contained"
            disabled={!newEnvironment.nombre.trim()}
          >
            Agregar Ambiente
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog open={newVersionDialog} onClose={() => setNewVersionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear Nueva Versión</DialogTitle>
        <DialogContent
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !saving && newVersionData.numero_version) {
              e.preventDefault();
              handleCreateVersion();
            }
          }}
        >
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="select-version-label">Versión base enviada *</InputLabel>
              <Select
                labelId="select-version-label"
                value={newVersionData.numero_version || ''}
                label="Versión base enviada *"
                onChange={e => setNewVersionData(prev => ({ ...prev, numero_version: e.target.value }))}
              >
                {formData.versions.filter(v => v.estado === 'sent').length === 0 && (
                  <MenuItem value="" disabled>No hay versiones enviadas</MenuItem>
                )}
                {formData.versions.filter(v => v.estado === 'sent').map(v => (
                  <MenuItem value={v.version_number} key={v.id}>
                    {`Versión ${v.version_number} - ${v.titulo}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewVersionDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreateVersion} variant="contained" disabled={saving || !newVersionData.numero_version}>
            {saving ? <CircularProgress size={20} /> : 'Crear Versión'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fecha prevista de entrega - checkpoint bloqueante (CLAUDE.md §5).
          No tiene botón de cancelar-y-seguir: o se confirma la fecha, o la
          transición no ocurre. Se cierra sin transicionar solo si el usuario
          desiste del cambio de estado entero. */}
      <Dialog
        open={deliveryPrompt.open}
        onClose={() => setDeliveryPrompt(prev => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Fecha prevista de entrega</DialogTitle>
        <DialogContent
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !saving && deliveryPrompt.value) {
              e.preventDefault();
              handleConfirmDeliveryDate();
            }
          }}
        >
          <Alert severity="info" sx={{ mb: 2 }}>
            {deliveryPrompt.target === 'sold'
              ? 'Confirmá la fecha prevista de entrega antes de marcar el presupuesto como vendido.'
              : 'Ingresá o confirmá la fecha prevista de entrega antes de enviar el presupuesto.'}
          </Alert>
          <TextField
            type="date"
            fullWidth
            label="Fecha prevista de entrega"
            InputLabelProps={{ shrink: true }}
            value={deliveryPrompt.value}
            onChange={e => setDeliveryPrompt(prev => ({ ...prev, value: e.target.value }))}
          />
          {/* Al vender, el mismo popup permite reconfirmar/editar el % H
              (CLAUDE.md §5, PO 2026-09-15). El valor viaja por el camino
              allow_sold tras la transición: 'sold' sigue 'sold'. */}
          {deliveryPrompt.target === 'sold' && (
            <TextField
              type="number"
              fullWidth
              sx={{ mt: 2 }}
              label="% Facturado"
              InputLabelProps={{ shrink: true }}
              value={deliveryPrompt.porcentajeFacturado}
              onChange={e => {
                const v = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                setDeliveryPrompt(prev => ({ ...prev, porcentajeFacturado: v }));
              }}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              helperText={`% H (sin factura): ${(100 - deliveryPrompt.porcentajeFacturado).toFixed(2)}%`}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryPrompt(prev => ({ ...prev, open: false }))}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDeliveryDate}
            variant="contained"
            disabled={saving || !deliveryPrompt.value}
          >
            {saving ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup de la Calculadora H (CLAUDE.md §5, PO 2026-09-15): split de
          facturación para screenshotear al cliente. Sólo lectura - el % H se
          edita arriba, en Proyecto y Cliente. */}
      <CalculadoraHModal
        open={hModalOpen}
        onClose={() => setHModalOpen(false)}
        materialesUsd={versionSummary ? Number(versionSummary.materiales_usd) : 0}
        ivaPercentage={versionSummary ? Number(versionSummary.iva_percentage) : 0}
        porcentajeH={currentPorcentajeH != null ? Number(currentPorcentajeH) : 100 - facturadoInput}
        hFxRate={currentVersion?.h_fx_rate != null ? Number(currentVersion.h_fx_rate) : null}
        hFxCurrency={currentVersion?.h_fx_currency}
      />

      {/* Rechazo (CLAUDE.md §5): motivo obligatorio. Al confirmar corre
          pending_approval -> rejected ("No Aprobado"). */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => { if (!saving) { setRejectDialog({ open: false, reason: '' }); setSelectedStatus(''); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rechazar presupuesto</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            El vendedor verá este motivo y podrá corregir y reenviar la versión.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            required
            label="Motivo del rechazo"
            value={rejectDialog.reason}
            onChange={e => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialog({ open: false, reason: '' }); setSelectedStatus(''); }} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            color="error"
            disabled={saving || !rejectDialog.reason.trim()}
          >
            {saving ? <CircularProgress size={20} /> : 'Rechazar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* "Enviar a ERP" (CLAUDE.md §8.1-§8.12): secciones + cliente + envío. */}
      <Dialog open={erpDialog} onClose={() => { if (!erpSubmitting) setErpDialog(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar a ERP</DialogTitle>
        <DialogContent>
          {erpResult ? (
            // Resultado del envío (ok:true o ok:false / parcial).
            <Box>
              <Alert severity={erpResult.ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {erpResult.ok
                  ? 'Presupuesto cargado en ERP.'
                  : (erpResult.error || 'El envío no se completó. Podés reintentar.')}
              </Alert>
              <Typography variant="body2">
                NP: {erpResult.erp_np_document_id || 'pendiente'}
              </Typography>
              {erpResult.nph_required && (
                <Typography variant="body2">
                  NPH: {erpResult.erp_nph_document_id || 'pendiente'}
                </Typography>
              )}
              {erpResult.unresolved_skus.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>SKUs sin resolver</Typography>
                  <Typography variant="body2">{erpResult.unresolved_skus.join(', ')}</Typography>
                </Alert>
              )}
            </Box>
          ) : erpProcessing ? (
            // El job sigue corriendo tras la ventana de espera (arranque en frío
            // del gateway). El rep puede cerrar; el resultado aparece en la
            // superficie pasiva del presupuesto cuando el job termina.
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography variant="body2">
                Procesando en segundo plano… Podés cerrar; el estado de carga en ERP
                se actualizará en el presupuesto cuando termine.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {erpError && <Alert severity="error">{erpError}</Alert>}

              {/* Secciones a facturar (§8.2). */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Secciones a facturar</Typography>
                <FormControlLabel
                  control={<Checkbox checked={erpSections.materiales}
                    onChange={e => setErpSections(prev => ({ ...prev, materiales: e.target.checked }))} />}
                  label="Materiales"
                />
                <FormControlLabel
                  control={<Checkbox checked={erpSections.mano_obra}
                    onChange={e => setErpSections(prev => ({ ...prev, mano_obra: e.target.checked }))} />}
                  label="Mano de Obra"
                />
                <FormControlLabel
                  control={<Checkbox checked={erpSections.flete}
                    onChange={e => setErpSections(prev => ({ ...prev, flete: e.target.checked }))} />}
                  label="Flete"
                />
              </Box>

              <Divider />

              {/* Cliente: buscar (§8.3) o crear manualmente. */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Cliente Bejerman</Typography>
                {erpSelectedCliente ? (
                  <Alert severity="success" action={
                    <Button color="inherit" size="small" onClick={() => setErpSelectedCliente(null)}>Cambiar</Button>
                  }>
                    {(erpSelectedCliente.Cliente_RazonSocial || '(sin razón social)')}
                    {erpSelectedCliente.Cliente_CUIT ? ` · ${erpSelectedCliente.Cliente_CUIT}` : ''}
                    {` · cód. ${erpSelectedCliente.Cliente_Codigo}`}
                  </Alert>
                ) : erpShowCreate ? (
                  <Stack spacing={2}>
                    <TextField label="Razón social" required fullWidth size="small"
                      value={erpCreateForm.razon_social}
                      onChange={e => setErpCreateForm(prev => ({ ...prev, razon_social: e.target.value }))} />
                    <TextField label="CUIT" required fullWidth size="small"
                      value={erpCreateForm.cuit}
                      onChange={e => setErpCreateForm(prev => ({ ...prev, cuit: e.target.value }))} />
                    <Box display="flex" gap={2}>
                      <TextField select label="Situación IVA" fullWidth size="small"
                        value={erpCreateForm.situacion_iva}
                        onChange={e => setErpCreateForm(prev => ({ ...prev, situacion_iva: e.target.value }))}>
                        {SITUACION_IVA_OPTIONS.map(o => (
                          <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField select label="Situación IIBB" fullWidth size="small"
                        value={erpCreateForm.situacion_iibb}
                        onChange={e => setErpCreateForm(prev => ({ ...prev, situacion_iibb: e.target.value }))}>
                        {SITUACION_IIBB_OPTIONS.map(o => (
                          <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box display="flex" gap={2}>
                      <TextField select label="Situación Ganancias" fullWidth size="small"
                        value={erpCreateForm.situacion_ganancias}
                        onChange={e => setErpCreateForm(prev => ({ ...prev, situacion_ganancias: e.target.value }))}>
                        {SITUACION_GANANCIAS_OPTIONS.map(o => (
                          <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField select label="Provincia" fullWidth size="small"
                        value={erpCreateForm.codigo_provincia}
                        onChange={e => setErpCreateForm(prev => ({ ...prev, codigo_provincia: e.target.value }))}>
                        {PROVINCIA_OPTIONS.map(o => (
                          <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box display="flex" gap={2}>
                      <TextField label="Cód. Vendedor" fullWidth size="small"
                        value={erpCreateForm.vendedor_codigo}
                        onChange={e => setErpCreateForm(prev => ({ ...prev, vendedor_codigo: e.target.value }))} />
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button variant="contained"
                        disabled={erpSubmitting || !erpCreateForm.razon_social.trim() || !erpCreateForm.cuit.trim()}
                        onClick={handleErpCreateCliente}>
                        {erpSubmitting ? <CircularProgress size={20} /> : 'Crear cliente'}
                      </Button>
                      <Button onClick={() => setErpShowCreate(false)}>Volver a buscar</Button>
                    </Box>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Box display="flex" gap={1}>
                      <TextField label="Buscar por CUIT o razón social" fullWidth size="small"
                        value={erpClienteQuery}
                        onChange={e => setErpClienteQuery(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleErpSearch(); } }} />
                      <Button variant="outlined" disabled={erpSearching || !erpClienteQuery.trim()}
                        onClick={handleErpSearch}>
                        {erpSearching ? <CircularProgress size={20} /> : 'Buscar'}
                      </Button>
                    </Box>
                    {erpMatches.map((c, i) => (
                      <Box key={c.Cliente_Codigo || i} display="flex" justifyContent="space-between"
                        alignItems="center" sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                        <Typography variant="body2">
                          {(c.Cliente_RazonSocial || '(sin razón social)')}
                          {c.Cliente_CUIT ? ` · ${c.Cliente_CUIT}` : ''}
                        </Typography>
                        <Button size="small" variant="contained" onClick={() => setErpSelectedCliente(c)}>
                          Elegir
                        </Button>
                      </Box>
                    ))}
                    <Button size="small" onClick={() => setErpShowCreate(true)}>
                      No está en la lista — crear cliente
                    </Button>
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErpDialog(false)} disabled={erpSubmitting}>
            {erpResult || erpProcessing ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!erpResult && !erpProcessing && (
            <Button variant="contained" onClick={handleErpSubmit}
              disabled={erpSubmitting || !erpSelectedCliente
                || !(erpSections.materiales || erpSections.mano_obra || erpSections.flete)}>
              {erpSubmitting ? <CircularProgress size={20} /> : 'Enviar a ERP'}
            </Button>
          )}
          {erpResult && !erpResult.ok && (
            <Button variant="contained" color="warning" onClick={() => setErpResult(null)}>
              Reintentar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Eliminar Presupuesto</DialogTitle>
        <DialogContent
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !saving) {
              e.preventDefault();
              handleDeleteBudget();
            }
          }}
        >
          <Alert severity="warning" sx={{ mb: 2 }}>Esta acción no se puede deshacer</Alert>
          <Typography>¿Estás seguro de que deseas eliminar el presupuesto <strong>#{formData.code}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancelar</Button>
          <Button onClick={handleDeleteBudget} variant="contained" color="error" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Eliminar Presupuesto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alerta de stock */}
      <Snackbar
        open={showAlertStock && !!alertStock}
        autoHideDuration={null}
        onClose={() => {
          setShowAlertStock(false);
          setAlertStock(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            minWidth: '400px',
            justifyContent: 'center',
            alignItems: 'center',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            position: 'fixed',
            zIndex: 1500,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            boxShadow: 8,
          }
        }}
      >
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setShowAlertStock(false);
                setAlertStock(null);
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            width: '100%',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            textAlign: 'center',
            bgcolor: '#d32f2f', // Rojo fuerte
            color: '#fff',
            border: '2px solid #b71c1c',
            boxShadow: 8,
          }}
        >
          {alertStock}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetDetailNew;
